import { ARTIST_PROFILES } from './artistProfiles';
import { isFrenchLanguage, isQuebecoisAudioAvailable } from '../utils/carrierResolver';
import { toGcsPublicUrl, GCS_PUBLIC_BUCKET } from '../utils/mediaPaths';

const CREW = {
  togee: {
    speaker: 'TogeeVision',
    register: 'Fixed Point',
  },
  lex: {
    speaker: 'Lex Gupta',
    register: 'Structural Read',
  },
  anansi: {
    speaker: 'Anansi Byte',
    register: 'Provenance Read',
  },
  kaelen: {
    speaker: 'Kaelen Vance',
    register: 'Blueprint Read',
  },
};

export const getTransmissionChronicleId = (sourceType, sourceId) => {
  const raw = `${sourceType || 'field'}-${sourceId || 'unknown'}`;
  return `transmission_${String(raw).toLowerCase().replace(/[^a-z0-9_.-]+/g, '_').replace(/^_+|_+$/g, '')}`;
};

export const catchArtistVenueAudioMismatch = (profileOrChronicle, audioUrl) => {
  if (!profileOrChronicle || !audioUrl) return { isMismatch: false, venueId: null, notice: null };

  const isArtist = profileOrChronicle.sourceType === 'artist' || profileOrChronicle.role?.includes('Artist') || String(profileOrChronicle.id || '').startsWith('artist-');
  const isVenueAudio = /\/transmission_venue-|\/venue-/.test(audioUrl);

  if (isArtist && isVenueAudio) {
    const match = audioUrl.match(/transmission_venue-([^._]+)/) || audioUrl.match(/venue-([^._]+)/);
    const venueSlug = match ? match[1] : 'associated-venue';
    const venueName = venueSlug.replace(/-/g, ' ').toUpperCase();
    return {
      isMismatch: true,
      venueId: `venue-${venueSlug}`,
      notice: `Receiver tuned to associated venue signal (${venueName}) while artist field capture is off-air.`,
    };
  }

  return { isMismatch: false, venueId: null, notice: null };
};

const buildArtistTransmissionChronicle = (profile) => {
  if (!profile) return null;

  const primaryFrequency = profile.frequency?.[0];
  const secondaryFrequency = profile.frequency?.[1];
  const primaryLineage = profile.lineage?.[0];
  const firstEcho = profile.fieldEchoes?.[0];

  const chronicleId = getTransmissionChronicleId('artist', profile.id);

  const primaryAudioUrl = profile.transmissionChronicleAudioUrl || `https://storage.googleapis.com/staging.togeevision-f3725.appspot.com/chronicles/2026/audio/2026-05-15_midnight_transmission_artist-${profile.id}.mp3`;
  const fallbackAudioUrl = profile.fallbackAudioUrl || `/audio/2026-05-15_midnight_transmission_artist-${profile.id}.mp3`;

  const audioMismatch = catchArtistVenueAudioMismatch(profile, primaryAudioUrl);

  const candidateUrls = [
    primaryAudioUrl,
    fallbackAudioUrl,
    `https://storage.googleapis.com/staging.togeevision-f3725.appspot.com/chronicles/2026/audio/2026-05-15_midnight_transmission_artist-${profile.id}.mp3`,
    `/audio/2026-05-15_midnight_transmission_artist-${profile.id}.mp3`,
  ];

  const associatedVenueId = profile.associatedVenueIds?.[0] || profile.upcomingNodes?.[0]?.venueId;
  if (associatedVenueId) {
    const venueSlug = String(associatedVenueId).replace(/^venue-/, '');
    candidateUrls.push(
      toGcsPublicUrl(GCS_PUBLIC_BUCKET, `audio/transmission_venue-${venueSlug}.mp3`),
    );
  }

  const candidates = Array.from(new Set(candidateUrls.filter(Boolean)));

  const visualAssets = [
    ...(profile.visualAssets || []),
    ...(profile.archiveItems || [])
      .filter((item) => item.type === 'image' && (item.url || item.src || item.accessUrl))
      .map((item) => ({
        id: item.id,
        type: 'image',
        src: item.accessUrl || item.url || item.src,
        title: item.title,
        caption: item.caption || item.title,
      })),
  ];
  const primaryImage = profile.image || visualAssets[0]?.src || null;

  return {
    id: `artist-${profile.id}`,
    chronicleId,
    sourceType: 'artist',
    sourceId: profile.id,
    city: profile.city,
    title: `${profile.name}: Transmission Chronicle`,
    eyebrow: `Artist Profile No. ${profile.profileNumber}`,
    subject: profile.name,
    status: profile.status,
    transmission: profile.transmission,
    sourcePath: `/artists/${profile.id}`,
    radarPath: profile.radarPath,
    image: primaryImage,
    imagePosition: profile.imagePosition || profile.objectPosition || visualAssets[0]?.imagePosition || null,
    imageAlt: profile.imageAlt || `${profile.name} field archive`,
    imageCaption: profile.imageCaption || null,
    visualAssets,
    archiveItems: profile.archiveItems || [],
    relatedTransmissions: profile.relatedTransmissions || [],
    audioUrl: primaryAudioUrl,
    fallbackAudioUrl,
    carriers: profile.carriers || undefined,
    translations: profile.translations || undefined,
    acousticTelemetry: profile.acousticTelemetry || undefined,
    candidates,
    isVenueFallback: audioMismatch.isMismatch,
    audioNotice: audioMismatch.notice,
    audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(chronicleId)}`,
    summary:
      profile.currentTransmission || profile.summary || `${profile.name} is carrying an active field signal.`,
    crewCritique: profile.crewCritique || [
      {
        ...CREW.togee,
        line: `${profile.name} is not decoration in the radar. This is a signal with a body: ${profile.role}.`,
      },
      {
        ...CREW.lex,
        line: primaryFrequency
          ? `${primaryFrequency.label}: ${primaryFrequency.value}`
          : `${profile.name} needs one clean archive path, one current schedule point, and one audience action that survives the feed.`,
      },
      {
        ...CREW.anansi,
        line: primaryLineage
          ? `The old root is visible: ${primaryLineage}`
          : `The archive has to name the lineage before the algorithm flattens it into genre wallpaper.`,
      },
      {
        ...CREW.kaelen,
        line: firstEcho || secondaryFrequency?.value || profile.integration?.body || profile.summary,
      },
    ].filter((item) => item.line),
    closing:
      profile.closing ||
      profile.integration?.body ||
      'The transmission becomes useful when the profile, radar entry, and room-level signal point to the same living archive.',
    provenance: profile.provenance || null,
    mediaArchaeology: profile.mediaArchaeology || {
      description: profile.bio || `${profile.name} is mapped as a live performance node. Their profile preserves artistic provenance—ensuring lineage, original repertoire, and direct community connections survive the flattening effect of stream algorithms.`,
      highlight: (profile.lineage && profile.lineage.length > 0)
        ? `Creative Lineage: ${profile.lineage.join(', ')}.`
        : 'Independent Signal Network.'
    },
  };
};

const PARCEL_5_TRANSMISSION = {
  id: 'venue-parcel-5',
  chronicleId: getTransmissionChronicleId('venue', 'parcel-5'),
  sourceType: 'venue',
  sourceId: 'parcel-5',
  city: 'rochester',
  title: 'Downtown United Presbyterian Church: Hallock Lounge Field Transmission',
  eyebrow: 'Rochester Fringe Festival / Hallock Lounge (Thursday, Sept 24 ⋅ 7:00 – 8:00pm)',
  subject: 'Rochester Fringe Festival: Mister Aubs Live at Hallock Lounge',
  status: 'Field Transmission Active',
  transmission: 'Mister Aubs Live at Downtown United Presbyterian Church: Hallock Lounge',
  sourcePath: '/passport/transmissions/venue-parcel-5',
  radarPath: '/passport/radar?city=rochester',
  image: '/images/transmissions/rochester-fringe/mister-aubs-live-performance.jpg',
  imagePosition: 'center top',
  imageAlt: 'Mister Aubs delivering live vocals at Downtown United Presbyterian Church: Hallock Lounge during Rochester Fringe Festival',
  audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-parcel-5.mp3'),
  fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-parcel-5.mp3'),
  carriers: {
    en: {
      audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-parcel-5.mp3'),
      audioReady: true,
    },
    'fr-CA': {
      audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-parcel-5_fr-ca.mp3'),
      audioReady: true,
    },
  },
  translations: {
    'fr-CA': {
      title: 'Downtown United Presbyterian Church : Transmission de terrain du salon Hallock Lounge',
      eyebrow: 'Festival Rochester Fringe / Hallock Lounge (Jeudi 24 sept. ⋅ 19h00 – 20h00)',
      subject: 'Rochester Fringe Festival : Mister Aubs en direct du Hallock Lounge',
      status: 'Transmission de terrain active',
      transmission: 'Mister Aubs en concert à la Downtown United Presbyterian Church : Hallock Lounge',
      summary:
        'Transmission de terrain du Rochester Fringe Festival captant Mister Aubs et son trio complice en direct à la Downtown United Presbyterian Church : Hallock Lounge le jeudi 24 septembre (19h00–20h00), reliée au corridor artistique de Rochester.',
      crewCritique: [
        {
          ...CREW.togee,
          line:
            'Rochester Fringe à la Downtown United Presbyterian Church : Hallock Lounge. Mister Aubs s\'avance au micro dans la salle du salon le jeudi soir, entouré de son trio devant le piano à queue sous les murs pourpres chaleureux. Quand la voix live et la présence consciente s\'emparent de la pièce, la fréquence passe d\'une architecture institutionnelle à une archive communautaire vivante.',
        },
        {
          ...CREW.lex,
          line:
            'Coordonnée vérifiée à la Downtown United Presbyterian Church : Hallock Lounge (121 N Fitzhugh St, Rochester, NY). Le programme capte la performance de Mister Aubs du jeudi avec une archéologie médiatique souveraine, reliant la session directement au radar du Passeport Gig de Rochester.',
        },
        {
          ...CREW.anansi,
          line:
            'Des presses d\'imprimerie du North Star de Frederick Douglass aux lignées jazz et soul de l\'East End, Rochester a toujours été une ville de signaux clandestins. La performance de Mister Aubs au Hallock Lounge perpétue cette continuité exacte — voix brutes, présence sans compromis et intimité immédiate avec la salle.',
        },
        {
          ...CREW.kaelen,
          line:
            'Télémétrie acoustique enregistrée en 44.1 kHz stéréo, volume intégré à -14.1 LUFS. La mise en scène entrelace la narration studio tech-noir et les prises directes sur la scène du Hallock Lounge.',
        },
      ],
      closing:
        'Le signal de Rochester est verrouillé. Du plancher du Hallock Lounge à la fréquence du centre-ville : son souverain, provenance authentique.',
      mediaArchaeology: {
        description:
          'Le Festival Rochester Fringe transforme le centre-ville en une grille de performance souveraine multi-salles. Cette transmission documente Mister Aubs en direct au salon Hallock Lounge de la Downtown United Presbyterian Church le jeudi 24 septembre (19h00–20h00), reliant l\'art de la chanson régionale au grand livre du Passeport Gig de Rochester.',
        highlight:
          'Downtown United Presbyterian Church (Hallock Lounge) : Session live de Mister Aubs, télémétrie stéréo 44.1 kHz et provenance communautaire souveraine.',
      },
    },
  },
  audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'parcel-5'))}`,
  summary:
    'Rochester Fringe Festival field transmission capturing Mister Aubs and his collaborator trio live at Downtown United Presbyterian Church: Hallock Lounge on Thursday, September 24 (7:00–8:00 PM), connected to the broader Rochester performance corridor.',
  crewCritique: [
    {
      ...CREW.togee,
      line:
        'Rochester Fringe at Downtown United Presbyterian Church: Hallock Lounge. Mister Aubs steps to the microphone in the lounge hall on Thursday evening, flanked by his collaborator trio in front of the grand piano under warm red walls. When live vocals and conscious presence claim the room, the frequency shifts from institutional architecture to living community archive.',
    },
    {
      ...CREW.lex,
      line:
        'Verified field coordinate at Downtown United Presbyterian Church: Hallock Lounge (121 N Fitzhugh St, Rochester, NY). Program captures Mister Aubs\'s Thursday 7:00–8:00 PM Rochester Fringe performance with sovereign media archaeology, linking the session directly to the Rochester Gig Passport radar.',
    },
    {
      ...CREW.anansi,
      line:
        'From Frederick Douglass\'s North Star printing presses to the East End jazz and soul lineages, Rochester has always been a city of underground signals. Mister Aubs\'s performance at Downtown United Presbyterian Church: Hallock Lounge carries that exact continuity—raw vocals, uncompromised presence, and immediate room intimacy.',
    },
    {
      ...CREW.kaelen,
      line:
        'Acoustic telemetry logged at 44.1kHz stereo, -14.1 LUFS integrated loudness, and 10.7 LU dynamic range. Staging weaves studio noir narration with live field samples from the Hallock Lounge stage.',
    },
  ],
  closing:
    'The Rochester signal is locked. From the Hallock Lounge floor to the downtown frequency: sovereign sound, true provenance.',
  mediaArchaeology: {
    description:
      'Rochester Fringe Festival transforms downtown Rochester into a multi-venue sovereign performance grid. This transmission documents Mister Aubs performing live in the Hallock Lounge at Downtown United Presbyterian Church on Thursday, September 24 (7:00–8:00 PM), linking regional songcraft and live performance directly to the Rochester Gig Passport coordinate ledger.',
    highlight:
      'Downtown United Presbyterian Church (Hallock Lounge): Mister Aubs live session, 44.1kHz stereo master telemetry, and sovereign community provenance.',
  },
  provenance: {
    sourceAuthority: 'live_field_capture',
    eventDate: '2026-09-24',
    nodeCoordinate: 'Downtown United Presbyterian Church: Hallock Lounge (121 N Fitzhugh St, Rochester, NY)',
    capturedBy: ['TogeeVision Field Unit'],
    sourceMaterials: [
      'Downtown United Presbyterian Church (Hallock Lounge) Live Performance Master (Sep 24 at 6-48 PM.m4a post-7:05 PM set, Google Drive 1N5ry2ZCODZ7kcsZ6nrxeBrkWRdhtUZFa, 00:02:45.36, 44.1kHz stereo, SHA-256: 3b9ff43ecb234139d132ab9b94d08279e1a0a862b876eef047d32c3f141c9452)',
      'Rochester Fringe Festival Evening Audio Archive (Sep 24 at 9-01 PM.m4a, Google Drive 1oNAWvc5eAZ83qd5MOjJ3WRp6AP4ktkgv, 01:47:50, 48kHz stereo HE-AAC, SHA-256: 9c7d9677fc23439361b885f45ca6885497fa2a1a066084afe694a40ce1bf09f6)',
      '4K HEVC Master Field Capture (PXL_20260924_234138891.mp4, Google Drive 1803ccYi5a4BXSf2gaybMseruASyqDDEV, 00:02:41, 48kHz stereo AAC, SHA-256: 61cd0fdf602290c480d3c0569d175026e9a8bf82ccf5285803041700551c1db6)',
      'Hallock Lounge Live Performance Stills (Mister Aubs at Microphone Stand & Grand Piano)',
      'Downtown Rochester & Parcel 5 Corridor Telemetry (PXL_20260923_211257462, PXL_20260923_233457188, PXL_20260924_011121322)',
    ],
    rightsStatus: 'sovereign_field_archive',
  },
  acousticTelemetry: {
    duration: '00:02:45.36',
    sampleRate: '44100 Hz',
    channels: 'stereo',
    meanVolumeDb: -14.1,
    peakVolumeDb: -1.0,
    loudnessRangeLu: 10.7,
    chapters: [
      {
        chapter: 1,
        timecode: '00:00:00',
        title: 'Black Star Fanfare & Stage Introduction of Mister Aubs',
        act: 'TogeeVision, Host & Mister Aubs',
        energyLevelDb: -14.1,
      },
      {
        chapter: 2,
        timecode: '00:01:05',
        title: 'Collaborator Trio Introduction: DK Keys & Anthony A.C. Collins',
        act: 'Mister Aubs, DK Keys & Anthony A.C. Collins',
        energyLevelDb: -13.8,
      },
      {
        chapter: 3,
        timecode: '00:01:43',
        title: 'Rochester Lineage & Live Grand Piano Songcraft',
        act: 'TogeeVision, Mister Aubs & DK Keys',
        energyLevelDb: -14.0,
      },
      {
        chapter: 4,
        timecode: '00:02:20',
        title: 'Sovereign Philosophy, Hallock Lounge Ovation & Outro Jingle',
        act: 'TogeeVision, Mister Aubs & Audience',
        energyLevelDb: -13.9,
      },
    ],
  },
  visualAssets: [
    {
      id: 'photo-mister-aubs-live-performance',
      type: 'image',
      src: '/images/transmissions/rochester-fringe/mister-aubs-live-performance.jpg',
      imagePosition: 'center top',
      title: 'Mister Aubs Live at the Microphone',
      caption:
        'Mister Aubs delivering live vocals in green cardigan and glasses at the microphone stand in front of the grand piano in the Hallock Lounge at Downtown United Presbyterian Church.',
    },
    {
      id: 'photo-mister-aubs-trio-stage',
      type: 'image',
      src: '/images/transmissions/rochester-fringe/mister-aubs-trio-stage.jpg',
      title: 'Mister Aubs & Collaborator Trio on Stage',
      caption:
        'Mister Aubs with his collaborator trio smiling on stage after the set in the Hallock Lounge at Downtown United Presbyterian Church.',
    },
    {
      id: 'photo-spiritus-christi-taylor-chapel',
      type: 'image',
      src: '/images/transmissions/rochester-fringe/spiritus-christi-taylor-chapel.jpg',
      title: 'Downtown United Presbyterian Church',
      caption:
        'Historic red brick facade of Downtown United Presbyterian Church (121 N Fitzhugh St, Rochester, NY) housing the Hallock Lounge.',
    },
    {
      id: 'photo-parcel-5-i-heart-roc',
      type: 'image',
      src: '/images/transmissions/rochester-fringe/parcel-5-i-heart-roc.jpg',
      title: 'I ❤️ ROC Landmark at Parcel 5',
      caption:
        'The iconic I ❤️ ROC sign and festival grounds at Parcel 5 in downtown Rochester.',
    },
    {
      id: 'photo-magic-mirrors-spiegeltent',
      type: 'image',
      src: '/images/transmissions/rochester-fringe/magic-mirrors-spiegeltent.jpg',
      title: 'Magic Mirrors Spiegeltent',
      caption:
        'Magic Mirrors Spiegeltent facade illuminated at night during Rochester Fringe Festival.',
    },
    {
      id: 'photo-spiegelgarden-night-lounge',
      type: 'image',
      src: '/images/transmissions/rochester-fringe/spiegelgarden-night-lounge.jpg',
      title: 'Spiegelgarden Night Atmosphere',
      caption:
        'Outdoor festival lounge at night with hanging colored lanterns, fire pits, and public screen.',
    },
    {
      id: 'photo-spiegelgarden-cutwater-lounge',
      type: 'image',
      src: '/images/transmissions/rochester-fringe/spiegelgarden-cutwater-lounge.jpg',
      title: 'Spiegelgarden Evening Courtyard & Tower',
      caption:
        'Evening festival courtyard outside the Cutwater tent with glowing lantern strings and the downtown tower illuminated against the dusk sky.',
    },
    {
      id: 'photo-fringe-info-store-plaza',
      type: 'image',
      src: '/images/transmissions/rochester-fringe/fringe-info-store-plaza.jpg',
      title: 'Fringe Info, Store & Game Plaza',
      caption:
        'Rochester Fringe Info and Store pavilion with Kettle Corn booth, bean bag toss boards, and purple light projections across the festival plaza.',
    },
  ],
};

const TURBO_HAUS_MONTREAL_TRANSMISSION = {
  id: 'venue-turbo-haus-montreal',
  chronicleId: getTransmissionChronicleId('venue', 'turbo-haus-montreal'),
  sourceType: 'venue',
  sourceId: 'turbo-haus-montreal',
  city: 'montreal',
  title: 'Turbo Haus & Bily Kun: Montreal Field Transmission',
  eyebrow: 'Montreal Field Transmission / Saint-Denis & Mont-Royal',
  subject: 'Turbo Haus & Bily Kun Bar',
  status: 'Field Transmission Active',
  transmission: 'Live Band Karaoke Finale, Neon Interview & Bily Kun Absinthe Excursion',
  sourcePath: '/passport/transmissions/venue-turbo-haus-montreal',
  radarPath: '/passport/radar?city=montreal',
  image: '/images/transmissions/montreal/turbo-haus-live-stage.jpg',
  imageAlt: 'Live performance at Turbo Haus under neon lights with live band, keyboardist, and singer at the microphone',
  audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-turbo-haus-montreal.mp3'),
  fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-turbo-haus-montreal.mp3'),
  carriers: {
    en: {
      audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-turbo-haus-montreal.mp3'),
      audioReady: true,
    },
    'fr-CA': {
      audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-turbo-haus-montreal_fr-ca.mp3'),
      audioReady: true,
    },
  },
  translations: {
    'fr-CA': {
      title: 'Turbo Haus & Bily Kun : Transmission de terrain de Montréal',
      eyebrow: 'Transmission de terrain de Montréal / Saint-Denis et Mont-Royal',
      subject: 'Turbo Haus et Bar Bily Kun',
      status: 'Transmission de terrain active',
      transmission: 'Finale du karaoké avec groupe live, entrevue de Neon et virée absinthe au Bily Kun',
      summary:
        'Transmission de terrain à Montréal documentant la dernière soirée de karaoké avec orchestre au Turbo Haus (2040, rue Saint-Denis), un entretien en profondeur avec Neon sur l\'embourgeoisement, les amendes pour bruit des condos et la résilience des salles indépendantes, ainsi qu\'une virée nocturne au bar Bily Kun sur l\'avenue du Mont-Royal explorant la pilsner tchèque et le rituel d\'absinthe québécoise traditionnelle.',
      crewCritique: [
        {
          ...CREW.togee,
          line:
            'Le Turbo Haus au 2040, rue Saint-Denis. Neon et l\'équipe sonore à la console, la section rythmique en direct qui soutient les chanteurs sur scène, et la fréquence qui s\'étire jusqu\'au Bily Kun sur Mont-Royal. Quand les promoteurs immobiliers et les amendes de bruit menacent les salles indépendantes, la communauté forge son propre blindage culturel : spectacles plus tôt, accès pour tous les âges et entraide locale. La radio pirate est bel et bien vivante à Montréal.',
        },
        {
          ...CREW.lex,
          line:
            'Coordonnées vérifiées au Turbo Haus (2040, rue Saint-Denis) et au Bily Kun (354, avenue du Mont-Royal E). Le programme relie la télémétrie en direct, l\'histoire de la résilience du lieu et la captation de quartier directement au radar du Passeport Gig de Montréal.',
        },
        {
          ...CREW.anansi,
          line:
            'De la lignée du Village Voice en 1988 jusqu\'à la finale de karaoké live de ce soir, la scène indépendante montréalaise maintient une continuité sans faille. La bande directe préserve l\'histoire orale de Neon, le rituel traditionnel de l\'absinthe et les voix brutes sur scène, sans altération algorithmique.',
        },
        {
          ...CREW.kaelen,
          line:
            'Télémétrie acoustique enregistrée en 44.1 kHz stéréo, volume moyen calibré et normalisation broadcast. La mise en ondes entrelace la narration studio tech-noir et les échantillons directs du Turbo Haus et du Bily Kun.',
        },
      ],
      closing:
        'Le signal de Montréal est verrouillé. De la scène du Turbo Haus au comptoir du Bily Kun : son souverain, provenance authentique.',
      mediaArchaeology: {
        description:
          'Le Turbo Haus (2040, rue Saint-Denis) et le Bily Kun (354, avenue du Mont-Royal E) représentent des ancrages névralgiques de la musique indépendante et de la culture de quartier à Montréal. Cette transmission documente la dernière résidence hebdomadaire de karaoké live, les stratégies de survie communautaire et l\'archivage sonore souverain.',
        highlight:
          'Turbo Haus & Bily Kun : Entrevue de Neon sur les plaintes de bruit et la résilience DIY, rituel de l\'absinthe québécoise et performance live en direct.',
      },
    },
  },
  audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'turbo-haus-montreal'))}`,
  summary:
    'Montreal field transmission documenting the final Live Band Karaoke night at Turbo Haus (2040 Rue Saint-Denis), an in-depth conversation with Neon on gentrification, condo noise citations, and venue resilience, alongside an after-hours excursion to Bily Kun Bar on Mont-Royal exploring Czech pilsner and traditional Quebec absinthe rituals.',
  crewCritique: [
    {
      ...CREW.togee,
      line:
        'Turbo Haus at 2040 Rue Saint-Denis. Neon and the sound crew running the board, live rhythm section backing singers on the stage, and the frequency extending up to Bily Kun on Mont-Royal. When property developers and noise fines threaten independent rooms, the community builds its own cultural armor: earlier sets, all-ages access, and mutual aid tabling. Pirate radio is alive and well in Montreal.',
    },
    {
      ...CREW.lex,
      line:
        'Verified coordinates at Turbo Haus (2040 Rue Saint-Denis) and Bily Kun (354 Avenue du Mont-Royal E). Program links live performance telemetry, venue resilience history, and neighborhood field capture directly to the Montreal Gig Passport radar.',
    },
    {
      ...CREW.anansi,
      line:
        'From the 1988 Village Voice lineage to tonight\'s live band karaoke finale, Montreal\'s independent music underground maintains unbroken continuity. Direct field tape captures Neon\'s oral history, the traditional absinthe louche ritual, and live stage vocals without third-party editorial sanitization.',
    },
    {
      ...CREW.kaelen,
      line:
        'Acoustic telemetry logged at 44.1kHz stereo, -17.5 dB mean volume, and balanced broadcast normalization. Staging weaves studio noir narration with live field samples from Turbo Haus and Bily Kun.',
    },
  ],
  closing:
    'The Montreal signal is locked. From the Turbo Haus stage to the Bily Kun counter: sovereign sound, true provenance.',
  mediaArchaeology: {
    description:
      'Turbo Haus (2040 Rue Saint-Denis) and Bily Kun (354 Ave du Mont-Royal E) represent load-bearing anchors of Montreal\'s independent music and neighborhood culture. This transmission documents the final weekly live band karaoke residency, grassroots venue survival strategies, and sovereign sound archiving.',
    highlight:
      'Turbo Haus & Bily Kun: Neon interview on noise citations and DIY resilience, traditional absinthe louche, and live band karaoke stage performance.',
  },
  provenance: {
    sourceAuthority: 'live_field_capture',
    eventDate: '2026-09-28',
    nodeCoordinate: 'Turbo Haus (2040 Rue Saint-Denis, Montreal, QC) & Bily Kun (354 Ave du Mont-Royal E, Montreal, QC)',
    capturedBy: ['TogeeVision Field Unit'],
    sourceMaterials: [
      'Turbo Haus Bar Session & Neon Interview (Sep 28 at 7-40 PM.m4a, Google Drive 1pBXIRWMDrR5echBUOFyBb0zi1d2Zki1q, 45:14 duration, 48kHz stereo AAC, SHA-256: 57cb47475eab5096b833644641de8e395b4d46960bebb60d9d5ca581e17a06ac)',
      'Bily Kun Excursion & Live Band Karaoke Finale (Sep 28 at 8-28 PM.m4a, Google Drive 1xeYYGK9m-bsQ4UIHhvfsYvLA5FKk4EIu, 01:20:54 duration, 48kHz stereo AAC, SHA-256: b8568acb71af04b41dbc6a4e317299d6f771698a6b5b14a2fb057718c8873225)',
      '4K HEVC Master Field Capture (PXL_20260929_011651849.mp4, Google Drive 1ksfF-ZeK0MgL1rjXPBtHqZmacsZFp9_o, 00:00:28 duration, 48kHz stereo AAC, SHA-256: 5155a59c13936dcc3fa9f597d06c0c908abcc4868a75fdee0c09cf6716a1d746)',
      'Turbo Haus Stage & Neon Stills (PXL_20260929_010153603.NIGHT.RAW-02.ORIGINAL~2.jpg, Google Drive 1dRz5XFGY4fGnfKyh3muNaQ5eCkg4FcbD, SHA-256: 9b9a417125cf4604f514fb991d27afa9527a7c53e98c85382831cb100c3a346f)',
      'Sound Booth Console & Sticker Mosaic (PXL_20260929_011957643.RAW-01.MP.jpg, Google Drive 1vbQMeLSmrybVOxTdTD3_4BUDVOtbx26h, SHA-256: 5acc327e218c4d1b48078afabc275852fe55293c983aa191fd7c12fa8aab90a3)',
      'Montreal Field Master Audio (transmission_venue-turbo-haus-montreal.mp3, 00:02:51.23, 44.1kHz stereo, SHA-256: 2c23438a70e044c6add6128cff07a15c22394835fddb703564637e2c9eb51299)',
    ],
    rightsStatus: 'sovereign_field_archive',
  },
  acousticTelemetry: {
    duration: '00:02:51.23',
    sampleRate: '44100 Hz',
    channels: 'stereo',
    meanVolumeDb: -17.5,
    peakVolumeDb: -1.3,
    chapters: [
      {
        chapter: 1,
        timecode: '00:00:00',
        title: 'Black Star Fanfare & Turbo Haus Scene Lock',
        act: 'TogeeVision & Turbo Haus Console',
        energyLevelDb: -17.0,
      },
      {
        chapter: 2,
        timecode: '00:00:54',
        title: 'Neon on Gentrification, Noise Citations & Venue Resilience',
        act: 'Neon & TogeeVision',
        energyLevelDb: -17.5,
      },
      {
        chapter: 3,
        timecode: '00:01:34',
        title: 'Bily Kun Excursion & Traditional Absinthe Ritual',
        act: 'TogeeVision & Bily Kun Bartender',
        energyLevelDb: -17.8,
      },
      {
        chapter: 4,
        timecode: '00:02:10',
        title: 'Live Band Karaoke Finale, Mutual Aid & Sovereign Sign-off',
        act: 'Live Band Ensemble, Vocalist & TogeeVision',
        energyLevelDb: -17.2,
      },
    ],
  },
  visualAssets: [
    {
      id: 'photo-turbo-haus-live-stage',
      type: 'image',
      src: '/images/transmissions/montreal/turbo-haus-live-stage.jpg',
      title: 'Turbo Haus Stage & Neon Grid',
      caption:
        'Live performance at Turbo Haus under the neon roof lighting, \'T O H\' letter frames, live singer at the microphone, keyboardist, and drum kit.',
    },
    {
      id: 'photo-turbo-haus-sound-booth',
      type: 'image',
      src: '/images/transmissions/montreal/turbo-haus-sound-booth.jpg',
      title: 'Turbo Haus Sound Engineer Console',
      caption:
        'Sound engineer desk covered in punk, metal, and indie venue sticker art in front of exposed brick and circular light projections.',
    },
    {
      id: 'photo-turbo-haus-vocalist-band',
      type: 'image',
      src: '/images/transmissions/montreal/turbo-haus-vocalist-band.jpg',
      title: 'Live Band Karaoke Vocalist on Stage',
      caption:
        'Female vocalist singing live backed by full live rhythm section, keyboardist, bassist, and drummer during the live band karaoke finale at Turbo Haus.',
    },
  ],
};

const LESCOGRIFFE_MONTREAL_TRANSMISSION = {
  id: 'venue-lescogriffe-montreal',
  chronicleId: getTransmissionChronicleId('venue', 'lescogriffe-montreal'),
  sourceType: 'venue',
  sourceId: 'lescogriffe-montreal',
  city: 'montreal',
  title: "Bar L'Escogriffe: Montreal Field Transmission",
  eyebrow: 'Montreal Field Transmission / Saint-Denis & Plateau-Mont-Royal',
  subject: "Bar L'Escogriffe: béatitude & Le bon vent (Le lancement d'album)",
  status: 'Field Transmission Active',
  transmission: 'Le Reflet du Miroir Album Launch & Progressive Rock Field Capture',
  sourcePath: '/passport/transmissions/venue-lescogriffe-montreal',
  radarPath: '/passport/radar?city=montreal',
  image: '/images/transmissions/montreal/lescogriffe-beatitude-live-stage.jpg',
  imageAlt: "béatitude performing live on stage at Bar L'Escogriffe in Montreal with electric guitar, synths, and stage lighting",
  audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-lescogriffe-montreal.mp3'),
  fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-lescogriffe-montreal.mp3'),
  carriers: {
    en: {
      audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-lescogriffe-montreal.mp3'),
      audioReady: true,
    },
    'fr-CA': {
      audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-lescogriffe-montreal_fr-ca.mp3'),
      audioReady: true,
    },
  },
  translations: {
    'fr-CA': {
      title: "Bar L'Escogriffe : Transmission de terrain de Montréal",
      eyebrow: 'Transmission de terrain de Montréal / Saint-Denis et Plateau-Mont-Royal',
      subject: "Bar L'Escogriffe : béatitude et Le bon vent (Le lancement d'album)",
      status: 'Transmission de terrain active',
      transmission: "Lancement d'album « Le Reflet du Miroir » et captation rock progressif en direct",
      summary:
        'Transmission de terrain au Bar L\'Escogriffe (4461, rue Saint-Denis) captant le quintette estrien béatitude et Le bon vent pour le lancement de l\'album « Le Reflet du Miroir » — une œuvre poétique continue de 37 minutes en sept parties, inscrite dans la lignée de L\'Heptade d\'Harmonium et Close to the Edge de Yes.',
      crewCritique: [
        {
          ...CREW.togee,
          line:
            'Le Bar L\'Escogriffe au 4461, rue Saint-Denis. Six ans de travail acharné, des dizaines de spectacles subversifs à travers le Québec et un an en studio aboutissent enfin ce soir : béatitude et Le bon vent dévoilent « Le Reflet du Miroir » sur les planches de l\'Esco. Une fresque de 37 minutes d\'un seul trait, sans interruption. Quand un quintette émergent d\'Estrie s\'approprie la flamme d\'Harmonium et de Yes avec une telle audace, le rock progressif québécois s\'affranchit du passé pour redevenir un feu d\'artifice contemporain.',
        },
        {
          ...CREW.lex,
          line:
            'Coordonnée de terrain vérifiée au Bar L\'Escogriffe (4461, rue Saint-Denis, Montréal). Le programme documente le spectacle du 29 septembre 2026 avec une archéologie médiatique souveraine, reliant l\'album et la captation de performance directement au radar du Passeport Gig de Montréal.',
        },
        {
          ...CREW.anansi,
          line:
            'De la grande tradition poétique progressive du Québec des années 70 jusqu\'aux scènes underground du Plateau d\'aujourd\'hui, la filiation est intacte. La bande directe préserve la virtuosité des musiciens, la ferveur de la salle et la dynamique en sept mouvements sans filtre algorithmique.',
        },
        {
          ...CREW.kaelen,
          line:
            'Télémétrie acoustique enregistrée en 44.1 kHz stéréo, niveau intégré à -14.0 LUFS. La mise en ondes entrelace la narration studio tech-noir et les prises directes sur la scène de l\'Escogriffe.',
        },
      ],
      closing:
        'Le signal de Montréal est verrouillé. Du plancher de l\'Esco à la fréquence du Plateau : son souverain, provenance authentique.',
      mediaArchaeology: {
        description:
          'Le Bar L\'Escogriffe (4461, rue Saint-Denis) est une institution emblématique du Plateau-Mont-Royal pour la musique alternative et émergente. Cette transmission documente le lancement du premier album de béatitude, « Le Reflet du Miroir », unissant poésie introspective et rock progressif québécois dans le grand livre du Passeport Gig.',
        highlight:
          'Bar L\'Escogriffe : Lancement de l\'album de béatitude et Le bon vent, fresque progressive de 37 minutes, son stéréo 44.1 kHz et provenance souveraine.',
      },
    },
  },
  audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'lescogriffe-montreal'))}`,
  summary:
    'Montreal field transmission capturing Sherbrooke progressive quintet béatitude and Le bon vent live at Bar L\'Escogriffe (4461 Rue Saint-Denis) for the launch of "Le Reflet du Miroir"—a poignant 37-minute, 7-part continuous progressive rock opus in the lineage of Harmonium\'s L\'Heptade and Yes\'s Close to the Edge.',
  crewCritique: [
    {
      ...CREW.togee,
      line:
        'Bar L\'Escogriffe at 4461 Rue Saint-Denis. Six years in the making, dozens of subversive performances across Quebec, and an entire year tracking in Estrie studios culminate tonight: béatitude and Le bon vent unveil "Le Reflet du Miroir" on the Esco stage. A continuous 37-minute, seven-part progressive rock opus recorded end-to-end. When a Sherbrooke quintet channels the lineage of Harmonium and Yes with this caliber of raw conviction, progressive rock sheds its museum glass and becomes urgent community frequency again.',
    },
    {
      ...CREW.lex,
      line:
        'Verified field coordinate at Bar L\'Escogriffe (4461 Rue Saint-Denis, Montreal). Program documents the September 29, 2026 album launch with sovereign media archaeology, linking performance telemetry and the 7-part continuous opus directly to the Montreal Gig Passport radar.',
    },
    {
      ...CREW.anansi,
      line:
        'From the historic 1970s Quebec progressive poetry tradition of Harmonium to the modern Saint-Denis basement circuit, the lineage holds unbroken. Direct field tape captures the live musicianship, crowd energy, and seamless thematic movements without commercial streaming dilution.',
    },
    {
      ...CREW.kaelen,
      line:
        'Acoustic telemetry logged at 44.1kHz stereo, -14.0 LUFS integrated loudness, and wide dynamic range. Staging weaves studio noir narration with live stage performance excerpts from Bar L\'Escogriffe.',
    },
  ],
  closing:
    'The Montreal signal is locked. From the Esco floor to the Plateau frequency: sovereign sound, true provenance.',
  mediaArchaeology: {
    description:
      'Bar L\'Escogriffe (4461 Rue Saint-Denis) is a storied Plateau-Mont-Royal underground rock anchor. This transmission documents the launch of béatitude\'s debut album "Le Reflet du Miroir", celebrating progressive rock craft, regional artistic sovereignty, and live community archiving.',
    highlight:
      'Bar L\'Escogriffe: béatitude & Le bon vent album launch, 37-minute continuous 7-part progressive opus, and 44.1kHz stereo master telemetry.',
  },
  provenance: {
    sourceAuthority: 'live_field_capture',
    eventDate: '2026-09-29',
    nodeCoordinate: 'Bar L\'Escogriffe (4461 Rue Saint-Denis, Montreal, QC)',
    capturedBy: ['TogeeVision Field Unit'],
    sourceMaterials: [
      'Bar L\'Escogriffe Live Performance & Album Launch Set 1 (lescogriffe-set-audio-01.m4a, Google Drive 1CYh6-UTjqvgMQLCvpG_-gKKXSux1wS4p, 48kHz stereo, SHA-256: fcdd9ff80030da40648247559c363fcdc9bc09da2fd1ba7bb624a812e4d2cd29)',
      'Bar L\'Escogriffe Live Performance & Album Launch Set 2 (lescogriffe-set-audio-02.m4a, Google Drive 13OVLBWSOxGKP841OY3ORBGQLYbNYn9nP, 48kHz stereo, SHA-256: 080e5262420a2abb68a27ff461024c13ba403013699049de13608512e9619f16)',
      '4K HEVC Live Performance Capture Part 1 (lescogriffe-live-performance-01.mp4, Google Drive 1vnhMxDQ0J7flOMflL3SamNLA-QkzhI7Z, 48kHz stereo, SHA-256: fbaf8d4a24acc4cc6e1d1dbabfd43997cde9072e87c6add7274467767412a446)',
      '4K HEVC Live Performance Capture Part 2 (lescogriffe-live-performance-02.mp4, Google Drive 1LLfPoIuMiXCtnT9wK3dvJNE9W3gFvR-5, 48kHz stereo, SHA-256: c0e65e9598a1b7e24d988373e673e0c01bfa4e95ba63f28a12df50ef88ccbf77)',
      '4K HEVC Live Performance Capture Part 3 (lescogriffe-live-performance-03.mp4, Google Drive 1fDeZ7SlkcXz1a-3CiX4GIZN4JWtsIru1, 48kHz stereo, SHA-256: f50dd837e7a63b1d860ea7103edcc8fe23af5756f38109c583363f2600885100)',
      'Bar L\'Escogriffe Stage & Facade Stills (lescogriffe-beatitude-live-stage.jpg, lescogriffe-exterior-poster-facade.jpg, lescogriffe-bar-interior-neon.jpg)',
      'Montreal Field Master Audio (transmission_venue-lescogriffe-montreal.mp3, 00:02:22.47, 44.1kHz stereo, SHA-256: 3fe14c0db9f1dc851ff106f0e21a2243d4f828773950efabfe6ee59ff3a19b88)',
    ],
    rightsStatus: 'sovereign_field_archive',
  },
  acousticTelemetry: {
    duration: '00:02:22.47',
    sampleRate: '44100 Hz',
    channels: 'stereo',
    meanVolumeDb: -14.0,
    peakVolumeDb: -1.0,
    chapters: [
      {
        chapter: 1,
        timecode: '00:00:00',
        title: 'Black Star Fanfare & Saint-Denis Scene Lock',
        act: 'TogeeVision',
        energyLevelDb: -14.0,
      },
      {
        chapter: 2,
        timecode: '00:00:48',
        title: 'Live Band Opening & Progressive Theme',
        act: 'béatitude & Le bon vent',
        energyLevelDb: -13.8,
      },
      {
        chapter: 3,
        timecode: '00:01:21',
        title: 'Estrie Lineage & Harmonium / Yes Poetic Opus',
        act: 'TogeeVision & béatitude',
        energyLevelDb: -14.0,
      },
      {
        chapter: 4,
        timecode: '00:01:52',
        title: 'Dynamic Crescendo, Room Ovation & Outro Jingle',
        act: 'béatitude, Audience & TogeeVision',
        energyLevelDb: -13.9,
      },
    ],
  },
  visualAssets: [
    {
      id: 'photo-lescogriffe-beatitude-live-stage',
      type: 'image',
      src: '/images/transmissions/montreal/lescogriffe-beatitude-live-stage.jpg',
      title: "béatitude Live on Stage at L'Escogriffe",
      caption:
        "béatitude guitarist and band members performing live on stage at Bar L'Escogriffe under atmospheric stage lighting during Le lancement d'album.",
    },
    {
      id: 'photo-lescogriffe-exterior-poster-facade',
      type: 'image',
      src: '/images/transmissions/montreal/lescogriffe-exterior-poster-facade.jpg',
      title: "Bar L'Escogriffe Exterior Facade & Show Poster",
      caption:
        "Exterior of Bar L'Escogriffe at 4461 Rue Saint-Denis featuring the concert poster for béatitude et Le bon vent.",
    },
    {
      id: 'photo-lescogriffe-bar-interior-neon',
      type: 'image',
      src: '/images/transmissions/montreal/lescogriffe-bar-interior-neon.jpg',
      title: "L'Esco Interior & Red Neon Room Geometry",
      caption:
        "Interior ambiance of Bar L'Escogriffe showing the bar counter, red neon lighting, and intimate venue atmosphere.",
    },
  ],
};

const MINUTE_MAPP_MONTREAL_TRANSMISSION = {
  id: 'venue-minute-mapp-montreal',
  chronicleId: getTransmissionChronicleId('venue', 'minute-mapp-montreal'),
  sourceType: 'node',
  pillar: 'node',
  primeBadge: 'Public Civic Node · Van Horne Viaduct',
  sourceId: 'minute-mapp-montreal',
  city: 'montreal',
  title: 'MINUTE_MAPP 2026: Montreal Field Transmission',
  eyebrow: 'Public Civic Node · Million Carpets and Tiles & Van Horne Skatepark',
  subject: 'MINUTE_MAPP 2026: CROSSROADS Projection Mapping',
  status: 'Public Urban Node Active',
  transmission: 'CROSSROADS 60-Second Architectural Projections & Mile End Facade Canvas',
  sourcePath: '/passport/transmissions/venue-minute-mapp-montreal',
  radarPath: '/passport/radar?city=montreal',
  image: '/images/transmissions/montreal/minute-mapp-night-crowd-projection.jpg',
  imageAlt: 'Night crowd and 60-second architectural mapping projection on the Million Tapis et Tuiles facade overlooking the Van Horne skatepark',
  audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-minute-mapp-montreal.mp3'),
  fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-minute-mapp-montreal.mp3'),
  carriers: {
    en: {
      audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-minute-mapp-montreal.mp3'),
      audioReady: true,
    },
    'fr-CA': {
      audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-minute-mapp-montreal_fr-ca.mp3'),
      audioReady: true,
    },
  },
  translations: {
    'fr-CA': {
      primeBadge: 'Nœud civique public · Viaduc Van Horne',
      title: 'MINUTE_MAPP 2026 : Transmission de terrain de Montréal',
      eyebrow: 'Nœud civique public · Million Tapis et Tuiles et Skatepark Van Horne',
      subject: 'MINUTE_MAPP 2026 : Projections architecturales CARREFOUR',
      status: 'Nœud urbain public actif',
      transmission: 'Projections architecturales de 60 secondes CARREFOUR et toile vivante du Mile End',
      summary:
        'Transmission de terrain captant la soirée d\'ouverture du 10e anniversaire de MINUTE_MAPP 2026 par MAPP_MTL dans le Mile End. L\'équipe de Radio Pirate Black Star analyse les discours d\'ouverture de Jason Rodi (Moment Factory), de la conseillère municipale Marie Plourde défendant le viaduc Van Horne et la mémoire de Cabane 77, et de Michel Lemieux décrivant le mapping comme des poèmes japonais éphémères en mouvement.',
      crewCritique: [
        {
          ...CREW.togee,
          line:
            'Le viaduc Van Horne dans le Mile End, face au Million Tapis et Tuiles. Des bancs rouges sur l\'asphalte mouillé, des projecteurs haute puissance calibrant des grilles 1920x1200 sur la brique centenaire. Pour la 10e édition de MINUTE_MAPP sous le thème Carrefours, l\'équipe de Black Star se rassemble pour décortiquer les enjeux urbains, la contre-culture et 25 ans d\'histoire de projection à Montréal.',
        },
        {
          ...CREW.lex,
          line:
            'Jason Rodi de Moment Factory a rappelé sur scène l\'arc historique avec Thien Vu Dang : en 2001, un moment visuel par semaine sur le web ; en 2004, les minutes de Thien, qui ont fusionné en minute-moments pour engendrer Minute MAPP. Ce soir, 98 créations de 5 pays—dont 58 de Montréal—illuminent la façade sans barrière élitiste, évaluées par Nova Studios et OxygenXM avec le vote en direct du public.',
        },
        {
          ...CREW.anansi,
          line:
            'La conseillère municipale Marie Plourde a lancé un cri du cœur pour sauver le viaduc Van Horne menacé de démolition, ce qui raserait le skatepark et la mémoire de Cabane 77, repaire historique de cinéastes indépendants libertaires. Les artistes ripostent par le signal : de Sonar par Eye for Ear de Brooklyn manipulant des simulations cymatiques sous TouchDesigner, au réseau mycélien de Julien et Marina célébrant le mariage du pixel et de la racine.',
        },
        {
          ...CREW.kaelen,
          line:
            'Michel Lemieux a magistralement cadré la discipline : du temps des projecteurs cathodiques à 3 tubes, le mapping architectural relevait de la pure utopie. Les créations de 60 secondes sont des poèmes japonais visuels—du graffiti en mouvement, éphémères dans la nuit mais gravés à jamais dans la mémoire urbaine de Montréal.',
        },
      ],
      closing:
        'Le signal de Montréal est verrouillé. De la façade du Million Tapis au viaduc Van Horne : son souverain, provenance authentique.',
      mediaArchaeology: {
        description:
          'MINUTE_MAPP (MAPP_MTL) est une plateforme montréalaise pionnière de projection mapping créée en 2013. Cette transmission documente la 10e édition CARREFOUR, reliant les discours d\'ouverture institutionnels et militants, la sauvegarde du viaduc Van Horne, et l\'émancipation de l\'art numérique dans l\'espace public.',
        highlight:
          'MINUTE_MAPP 2026 : Discours d\'ouverture de Jason Rodi, Marie Plourde et Michel Lemieux, 98 créations internationales de 60 secondes, télémétrie multi-voix en direct.',
      },
    },
  },
  audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'minute-mapp-montreal'))}`,
  summary:
    'Montreal field transmission capturing the 10th anniversary opening of MINUTE_MAPP 2026 by MAPP_MTL in Mile End. The Black Star crew round-table breaks down festival opening remarks by Jason Rodi (Moment Factory), City Councillor Marie Plourde\'s battle to preserve the Van Horne Viaduct and Cabane 77 counter-culture incubator, and Michel Lemieux\'s framing of 60-second architectural mappings as ephemeral Japanese visual poems.',
  crewCritique: [
    {
      ...CREW.togee,
      line:
        'Autumn dusk at the Van Horne viaduct overlooking the skatepark in Mile End. Red plastic stools on rain-slicked pavement facing the industrial brick facade of the former Million Carpets and Tiles building. Calibration grids at 1920x1200 firing across the street for the 10th anniversary CROSSROADS edition. The Black Star crew convenes to document the openings, urban stakes, and 25-year projection lineage.',
    },
    {
      ...CREW.lex,
      line:
        'Jason Rodi of Moment Factory took the stage to trace the 25-year lineage with director Thien Vu Dang: from Moment Factory\'s 2001 weekly web moments to Thien\'s 2004 minutes, converging into minute-moments and birthing Minute MAPP. Tonight features 98 creations across 5 countries (58 from Montreal) open to all creators, adjudicated by partners Nova Studios and OxygenXM alongside live open stream voting.',
    },
    {
      ...CREW.anansi,
      line:
        'Mile End Councillor Marie Plourde issued an urgent rally call: the Van Horne Viaduct faces potential demolition by transport authorities, endangering the skatepark, basketball courts, and the anarchist incubator legacy of Cabane 77. Creators reclaim that concrete tonight: from Brian\'s Sonar with Eye for Ear (Brooklyn) using TouchDesigner cymatic echolocation, to Julien and Marina\'s mycelium network bridging forest roots and the web.',
    },
    {
      ...CREW.kaelen,
      line:
        'MAPP President Michel Lemieux reflected on the leap from vintage 3-tube CRT projectors to monumental facade mapping. He defined these 60-second pieces as Japanese visual poems—moving graffiti born of the street, ephemeral in the night air yet permanently etched into the cultural memory of Montreal.',
    },
  ],
  closing:
    'The Montreal signal is locked. From the Million Carpets facade to the Van Horne viaduct: sovereign sound, true provenance.',
  mediaArchaeology: {
    description:
      'MINUTE_MAPP (MAPP_MTL) is Montreal\'s pioneering projection mapping platform founded in 2013. This transmission archives the 10th anniversary CROSSROADS edition, integrating institutional and grassroots opening speeches, urban viaduct preservation struggles, and sovereign public digital art.',
    highlight:
      'MINUTE_MAPP 2026: Opening speeches by Jason Rodi, Marie Plourde, and Michel Lemieux, 98 global 60-second projections, and multi-voice Black Star studio critique.',
  },
  provenance: {
    sourceAuthority: 'sovereign_field_archive',
    eventDate: '2026-10-01',
    nodeCoordinate: 'Former Million Carpets and Tiles (Van Horne Skatepark, Mile End, Montreal, QC)',
    capturedBy: ['TogeeVision Field Unit'],
    sourceMaterials: [
      'MAPP_MTL MINUTE_MAPP 2026 Evening Audio Archive (minute-mapp-raw-audio.m4a, Google Drive 1sTPZVZyaVMNaWDMaqpLONO6o9sQFySXj, 48kHz stereo)',
      'Pixel 9 Visual Documentation & Calibration Stills (Google Drive 1BDF0QxS0wu3s91IfdcAQ0wvQV1d8wCNG, 1MhMscXmyRKpU1t7jRTpdjiqBgHXRai8y, 1UN1kV3NjcXwQPrHUEwmS41wuai-Y7JEf, 1sZ3DcSiKZtFyPt2hWHYUXNaAX9XMEdqM)',
      'Montreal Field Multi-Voice Master Audio (transmission_venue-minute-mapp-montreal.mp3, 00:03:44.05, 44.1kHz stereo, LUFS -14.0, True Peak -1.0 dBFS)',
      'Montreal Field Multi-Voice Master Audio fr-CA (transmission_venue-minute-mapp-montreal_fr-ca.mp3, 00:03:44.05, 44.1kHz stereo, LUFS -14.0, True Peak -1.0 dBFS)',
    ],
    rightsStatus: 'sovereign_field_archive',
  },
  acousticTelemetry: {
    duration: '00:03:44.05',
    sampleRate: '44100 Hz',
    channels: 'stereo',
    meanVolumeDb: -14.0,
    peakVolumeDb: -1.0,
    chapters: [
      {
        chapter: 1,
        timecode: '00:00:00',
        title: 'Black Star Fanfare & Station ID Lock',
        act: 'Station Dispatch & TogeeVision',
        energyLevelDb: -14.0,
      },
      {
        chapter: 2,
        timecode: '00:00:19',
        title: 'Van Horne Viaduct Dusk & 10th Edition Frame',
        act: 'TogeeVision Anchor Frame',
        energyLevelDb: -14.0,
      },
      {
        chapter: 3,
        timecode: '00:00:52',
        title: 'Stage Opening Announcement & Grounds Atmosphere',
        act: 'MAPP MTL Stage MC & Audience',
        energyLevelDb: -13.8,
      },
      {
        chapter: 4,
        timecode: '00:01:04',
        title: 'Jason Rodi & 25-Year Lineage (Moment Factory to Minute MAPP)',
        act: 'Lex Gupta Historical Critique',
        energyLevelDb: -14.0,
      },
      {
        chapter: 5,
        timecode: '00:01:42',
        title: 'Live Electronic Mapping Soundscape & Sub-Bass Pulse',
        act: 'MINUTE_MAPP Architectural Projections',
        energyLevelDb: -13.7,
      },
      {
        chapter: 6,
        timecode: '00:01:57',
        title: 'Marie Plourde Viaduct Defense, Cabane 77 & Generative Echolocation',
        act: 'Anansi Byte Urban Analysis',
        energyLevelDb: -14.0,
      },
      {
        chapter: 7,
        timecode: '00:02:44',
        title: 'Dynamic Mapping Crescendo & Cymatic Synthesis',
        act: 'Sonar (Eye for Ear) & Bio-Digital Projections',
        energyLevelDb: -13.6,
      },
      {
        chapter: 8,
        timecode: '00:02:59',
        title: 'Michel Lemieux on 3-Tube Projectors & Japanese Visual Poems',
        act: 'Kaelen Vance Aesthetic Critique',
        energyLevelDb: -14.0,
      },
      {
        chapter: 9,
        timecode: '00:03:32',
        title: 'Sovereign Archive Sign-off, Audience Ovation & Outro Stinger',
        act: 'TogeeVision & Mile End Audience',
        energyLevelDb: -13.9,
      },
    ],
  },
  visualAssets: [
    {
      id: 'photo-minute-mapp-night-crowd-projection',
      type: 'image',
      src: '/images/transmissions/montreal/minute-mapp-night-crowd-projection.jpg',
      title: 'Night Crowd & MINUTE_MAPP Projection',
      caption:
        'Audience gathered at the Van Horne skatepark viewing 60-second architectural mapping projections across the former Million Carpets and Tiles facade.',
    },
    {
      id: 'photo-minute-mapp-crowd-viaduct-evening',
      type: 'image',
      src: '/images/transmissions/montreal/minute-mapp-crowd-viaduct-evening.jpg',
      title: 'Van Horne Viaduct Audience & Opening Crowd',
      caption:
        'Crowd seated on red spectator stools beneath the Van Horne viaduct during the opening ceremony and festival speeches.',
    },
    {
      id: 'photo-minute-mapp-facade-projection-live',
      type: 'image',
      src: '/images/transmissions/montreal/minute-mapp-facade-projection-live.jpg',
      title: 'Live Facade Architectural Mapping',
      caption:
        'High-lumen projection animating the structural relief and windows of the former Million Carpets and Tiles building.',
    },
    {
      id: 'photo-minute-mapp-grid-calibration-facade',
      type: 'image',
      src: '/images/transmissions/montreal/minute-mapp-grid-calibration-facade.jpg',
      title: 'Checkered Mapping Calibration Grid',
      caption:
        'Daylight calibration grid projected across the industrial facade of Million Carpets and Tiles with rows of red spectator stools.',
    },
    {
      id: 'photo-minute-mapp-door-calibration-signal',
      type: 'image',
      src: '/images/transmissions/montreal/minute-mapp-door-calibration-signal.jpg',
      title: 'Projector Signal Resolution Calibration',
      caption:
        'Close-up of the 1920x1200@59.95 Hz input test pattern aligned against the weathered facade door.',
    },
    {
      id: 'photo-minute-mapp-projector-enclosure-vanhorne',
      type: 'image',
      src: '/images/transmissions/montreal/minute-mapp-projector-enclosure-vanhorne.jpg',
      title: 'Weather-Sealed Outdoor Projection Unit',
      caption:
        'Heavy-duty projector enclosure positioned between skatepark bollards and red stools, beaming across the street.',
    },
    {
      id: 'photo-minute-mapp-ten-year-facade-perspective',
      type: 'image',
      src: '/images/transmissions/montreal/minute-mapp-ten-year-facade-perspective.jpg',
      title: 'MAPP DIX Facade Perspective',
      caption:
        'Wide perspective of the MINUTE_MAPP facade display spanning the length of the building along the rain-soaked Mile End street.',
    },
    {
      id: 'photo-minute-mapp-raw-capture-facade',
      type: 'image',
      src: '/images/transmissions/montreal/minute-mapp-raw-capture-facade.jpg',
      title: 'High-Dynamic Raw Field Capture',
      caption:
        'Direct optical raw sensor capture documenting illumination contrast against the damp urban pavement.',
    },
  ],
};

const VENUE_TRANSMISSION_CHRONICLES = {
  'venue-turbo-haus-montreal': TURBO_HAUS_MONTREAL_TRANSMISSION,
  'venue-lescogriffe-montreal': LESCOGRIFFE_MONTREAL_TRANSMISSION,
  'venue-kismat-washington-heights': {
    id: 'venue-kismat-washington-heights',
    chronicleId: getTransmissionChronicleId('venue', 'kismat-washington-heights'),
    sourceType: 'venue',
    sourceId: 'kismat-washington-heights',
    city: 'nyc',
    title: 'Kismat Washington Heights: Transmission Chronicle',
    eyebrow: 'NYC Field Transmission No. 1',
    subject: 'Kismat: A Room With a Signal (with Jeremy)',
    status: 'Field Transmission Active',
    transmission: 'Kismat: A Room With a Signal — Jeremy & the Washington Heights Incubator',
    sourcePath: '/kismat',
    radarPath: '/passport/radar?city=nyc',
    image: '/images/kismat_washington_heights_signal.png',
    imageAlt: 'Kismat Washington Heights field signal — Jeremy and the Uptown Cultural Hub',
    imageCaption: 'NYC Field Transmission No. 1: Kismat (603 Fort Washington Ave), where Jeremy stewards the Washington Heights incubator for Tuesday Jazz Nights with Gottfried Stöger, Friday live music, and late-night karaoke.',
    audioUrl: 'https://storage.googleapis.com/staging.togeevision-f3725.appspot.com/chronicles/2026/audio/2026-05-15_midnight_transmission_venue-kismat-washington-heights.mp3',
    audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'kismat-washington-heights'))}`,
    carriers: {
      en: {
        audioUrl: 'https://storage.googleapis.com/staging.togeevision-f3725.appspot.com/chronicles/2026/audio/2026-05-15_midnight_transmission_venue-kismat-washington-heights.mp3',
        audioReady: true,
      },
      'fr-CA': {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-kismat-washington-heights_fr-ca.mp3'),
        audioReady: true,
      },
    },
    translations: {
      'fr-CA': {
        title: 'Kismat Washington Heights : Chronique de transmission',
        eyebrow: 'Transmission de terrain de NYC n° 1',
        subject: 'Kismat : Une salle avec un signal (avec Jeremy)',
        status: 'Transmission de terrain active',
        transmission: 'Kismat : Une salle avec un signal — Jeremy et l\'incubateur de Washington Heights',
        summary:
          'Stewardé par Jeremy comme un « carrefour culturel » et un incubateur de quartier au 603 Fort Washington Ave, Kismat réunit les mardis jazz avec le maître des bois résident Gottfried Stöger, les concerts du vendredi, les musiciens de Broadway et du Lincoln Center, et le karaoké communautaire.',
        crewCritique: [
          {
            ...CREW.togee,
            line:
              'Kismat est le contre-signal : une salle assez intime pour se souvenir des prénoms, assez vibrante pour prouver que la culture a toujours besoin d\'un plancher.',
          },
          {
            ...CREW.lex,
            line:
              'Le levier est concret : un profil de lieu avec Jeremy, un point d\'accès QR, une grille horaire reproductible qui transforme les habitués en participants actifs plutôt qu\'en trafic passif.',
          },
          {
            ...CREW.anansi,
            line:
              'Le nom porte la leçon. Kismat, c\'est le destin, mais la salle transforme le hasard en rituel quand les mêmes voisins continuent de s\'y retrouver.',
          },
          {
            ...CREW.kaelen,
            line:
              'Le schéma directeur est un nœud culturel local : scène, archive, profil d\'artiste et coordonnées radar fonctionnant comme un seul instrument civique.',
          },
        ],
        closing:
          'La transmission de terrain réussit quand la salle ne se contente plus d\'être repérable : elle devient lisible, accessible et digne d\'y revenir.',
        mediaArchaeology: {
          description:
            'Jeremy décrit Kismat comme un carrefour culturel conçu pour permettre aux gens de se mêler et de créer. À Washington Heights, la salle puise dans une densité exceptionnelle de talents locaux — musiciens de Broadway, du Lincoln Center, Gottfried Stöger et Louise Rogers — où l\'intrication quantique du vendredi soir forge la solidarité du quartier (Le Chéile).',
          highlight:
            'L\'Incubateur de Washington Heights : intrication quantique, Le Chéile et échelle humaine de la culture.',
        },
      },
    },
    summary:
      'Stewarded by Jeremy as a "Cultural Hub" and neighborhood incubator at 603 Fort Washington Ave, Kismat is where Tuesday Jazz Nights with resident woodwind master Gottfried Stöger, Friday live music, working Broadway and Lincoln Center players, and late-night karaoke become uptown community theater.',
    sections: [
      {
        eyebrow: 'Section 1',
        title: 'The Incubator',
        body:
          'Jeremy describes Kismat as a "Cultural Hub" designed for one purpose: to let people mingle and make stuff. It’s an incubator where a karaoke regular can evolve into a bandleader in under two years, proving that the room is a starting point, not just a destination.',
      },
      {
        eyebrow: 'Section 2',
        title: 'The Natural Resource',
        body:
          'Washington Heights holds a massive density of talent—Broadway pros, Lincoln Center players, resident woodwind master Gottfried Stöger, and "National Jazz Heroes" like Louise Rogers. Kismat taps into this local resource, providing a stage for the neighborhood’s world-class residents to play where they live.',
      },
      {
        eyebrow: 'Section 3',
        title: 'Resonance Frequency',
        body:
          'Friday live music here is about "Quantum Entanglement." When the room syncs up, it creates a physical resonance—the goosebumps that happen when players and audience vibrate at the same frequency. It’s the raw, unpolished energy of a room "nailing it."',
      },
      {
        eyebrow: 'Section 4',
        title: 'The Neighborhood Anchor',
        body:
          'It is the "Cheers" of the Heights. Beyond the music, the room succeeds because of neighborly ownership. It is a space where the "Togetherness" (Le Chéile) of the community is forged through shared glassware and high-stakes performance.',
      },
      {
        eyebrow: 'Section 5',
        title: 'The Signal',
        body:
          'The Black Star Signal starts here because Kismat represents the "human scale" of culture. By documenting these field transmissions, we preserve the heartbeat of a room that remains stubbornly authentic in a city of polished content.',
      },
    ],
    relatedTransmissions: [
      {
        id: 'artist-gottfried-stoger',
        eyebrow: 'Kismat Resident Woodwinds · Artist Profile No. 002',
        title: 'Gottfried Stöger: Kismat Resident Signal & Woodwind Field Transmission',
        description:
          'Explore resident woodwind master Gottfried Stöger’s Field Transmission—bridging Salzburg classical lineage, Broadway woodwind chairs, DREAMHUNTER, and weekly Tuesday Jazz Nights & Friday sets at Kismat.',
        path: '/passport/transmissions/artist-gottfried-stoger',
        ctaLabel: 'Open Gottfried Stöger Field Transmission →',
      },
      {
        id: 'kismat-room-profile-jeremy',
        eyebrow: 'Kismat Room Profile & Live Schedule · NYC-01',
        title: 'Kismat: A Room With a Signal — Jeremy’s Cultural Hub & Live Calendar',
        description:
          'Open the dedicated Kismat Washington Heights room page featuring Jeremy’s five-pillar incubator manifesto and the live Kismat Jazz & Karaoke horizon calendar.',
        path: '/kismat',
        ctaLabel: 'Open Kismat Profile & Calendar →',
      },
      {
        id: 'artist-monk-washington',
        eyebrow: 'Uptown & LES Resident Signal · Artist Profile No. 001',
        title: 'Monk Americana: NYC Field Transmission No. 1',
        description:
          'Listen to Monk Americana’s field transmission connecting Kismat in Washington Heights with Victoria NYC and Pianos on the Lower East Side.',
        path: '/passport/transmissions/artist-monk-washington',
        ctaLabel: 'Open Monk Americana Field Transmission →',
      },
      {
        id: 'artist-ed-marshall',
        eyebrow: 'Kismat Visual & Sonic Provenance · Artist Profile No. 003',
        title: 'Ed Marshall: Street Photojournalism & Kismat Sonic Archive',
        description:
          'Explore Ed Marshall’s field transmission documenting the visual and sonic provenance of Washington Heights and Kismat.',
        path: '/passport/transmissions/artist-ed-marshall',
        ctaLabel: 'Open Ed Marshall Field Transmission →',
      },
    ],
    visualAssets: [
      {
        id: 'kismat-washington-heights-signal-hero',
        type: 'image',
        src: '/images/kismat_washington_heights_signal.png',
        title: 'Kismat: A Room With a Signal — Washington Heights Cultural Hub',
        caption:
          'NYC Field Transmission No. 1: Kismat in Washington Heights, stewarded by Jeremy as a neighborhood incubator where karaoke regulars, Broadway & Lincoln Center players, and resident woodwind master Gottfried Stöger converge.',
      },
      {
        id: 'kismat-cadre-mule-flyer-2026-09-11',
        type: 'image',
        src: '/images/cadre-mule-kismat-2026-09-11.jpg',
        title: 'Friday Live Jazz & Karaoke Hosted by Jeremy at Kismat',
        caption:
          'Friday night session flyer from Kismat (603 Fort Washington Ave): live jazz followed by late-night neighborhood Karaoke hosted by Jeremy.',
      },
    ],
    crewCritique: [
      {
        ...CREW.togee,
        line:
          'Kismat is the counter-signal: a room small enough to remember names, loud enough to prove culture still needs a floor.',
      },
      {
        ...CREW.lex,
        line:
          'The lever is practical: one venue profile with Jeremy as steward, one QR path, one repeatable schedule surface that lets regulars become participants instead of passive traffic.',
      },
      {
        ...CREW.anansi,
        line:
          'The name carries the lesson. Kismat is chance, but the room turns chance into ritual when the same neighbors keep showing up.',
      },
      {
        ...CREW.kaelen,
        line:
          'The blueprint is a local culture node: stage, archive, artist profile, and radar coordinate working as one civic instrument.',
      },
    ],
    closing:
      'The field transmission succeeds when the room is no longer only discoverable. It becomes legible, reachable, and worth returning to.',
    mediaArchaeology: {
      description:
        'Jeremy describes Kismat as a "Cultural Hub" designed for one purpose: to let people mingle and make stuff. Tapping into Washington Heights’ natural resource of Broadway pros, Lincoln Center players, resident woodwind master Gottfried Stöger, and jazz heroes like Louise Rogers, Friday nights achieve "Quantum Entanglement"—forging the neighborhood togetherness (Le Chéile) of the Heights at human scale.',
      highlight: 'The Washington Heights Incubator: Quantum Entanglement, Le Chéile, and the human scale of culture.',
    },
  },
  'venue-house-of-rastafari': {
    id: 'venue-house-of-rastafari',
    chronicleId: getTransmissionChronicleId('venue', 'house-of-rastafari'),
    sourceType: 'venue',
    sourceId: 'house-of-rastafari',
    city: 'rtm',
    title: 'House of Rastafari: Transmission Chronicle',
    eyebrow: 'Rototom Venue Transmission No. 1',
    subject: 'House of Rastafari Stage',
    status: 'Field Transmission Active',
    transmission: 'House of Rastafari / Rototom Signal',
    sourcePath: '/passport/transmissions/venue-house-of-rastafari',
    radarPath: '/passport/radar?city=rtm',
    audioUrl: null,
    audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'house-of-rastafari'))}`,
    summary:
      'The House of Rastafari is a cultural hub at Rototom Sunsplash dedicated to roots reggae, spiritual lineage documentation, and dub session archiving.',
    crewCritique: [
      {
        ...CREW.togee,
        line:
          'House of Rastafari represents the spiritual and rhythmic center: preserving the sound system culture as a load-bearing community archive.',
      },
      {
        ...CREW.lex,
        line:
          'Staging is verified: one curatorial lead, custom QR check-in flows, and a digital schedule that directly pings the Yetibeb Moged channel.',
      },
    ],
    closing:
      'The Rototom signal is active. The transmission bridges distance and keeps roots history clear.',
    mediaArchaeology: {
      description: 'The House of Rastafari is a cultural hub at Rototom Sunsplash dedicated to roots reggae, spiritual lineage documentation, and dub session archiving.',
      highlight: 'Roots & Dub Sound System Archive: analog delays, dubplates, and space echo heritage.'
    },
  },
  'venue-steelstacks': {
    id: 'venue-steelstacks',
    chronicleId: getTransmissionChronicleId('venue', 'steelstacks'),
    sourceType: 'venue',
    sourceId: 'steelstacks',
    city: 'lehigh_valley',
    title: 'SteelStacks Bethlehem: Transmission Chronicle',
    eyebrow: 'Lehigh Valley Venue Transmission No. 1',
    subject: 'SteelStacks Bethlehem',
    status: 'Field Transmission Active',
    transmission: 'SteelStacks / Musikfest Signal',
    sourcePath: '/passport/transmissions/venue-steelstacks',
    radarPath: '/passport/radar?city=lehigh_valley',
    audioUrl: null,
    audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'steelstacks'))}`,
    summary:
      'SteelStacks is the industrial-arts anchor for Lehigh Valley Musikfest, hosting regional radars and community attendance check-ins.',
    crewCritique: [
      {
        ...CREW.togee,
        line:
          'SteelStacks grounds the regional network: converting industrial space into performance nodes that map user attendance.',
      },
    ],
    closing:
      'The Lehigh Valley signal loop is complete when local presence maps back to the central coordinate ledger.',
    mediaArchaeology: {
      description: 'SteelStacks Bethlehem transforms historical industrial infrastructure into an active performance node. By mapping physical check-ins and regional radars, it anchors the Musikfest signal network in local community geography.',
      highlight: 'Industrial Performance Architecture: historic furnaces, regional radar, and presence tracking.'
    },
  },
  'venue-victoria-nyc': {
    id: 'venue-victoria-nyc',
    chronicleId: getTransmissionChronicleId('venue', 'victoria-nyc'),
    sourceType: 'venue',
    sourceId: 'victoria-nyc',
    city: 'nyc',
    title: 'Victoria NYC: Field Transmission Chronicle',
    eyebrow: 'NYC Field Transmission / Lower East Side',
    subject: 'Victoria NYC',
    status: 'Field Transmission Active',
    transmission: "Miwi's Music Thing & Alex Orange Drink Birthday Sunday Session",
    sourcePath: '/passport/transmissions/venue-victoria-nyc',
    radarPath: '/passport/radar?city=nyc',
    image: '/images/miwi-music-thing-victoria-nyc-2026-09-13.jpg',
    imageAlt: "Miwi's Music Thing at Victoria Bar with Special Guests Heidemann and Baby Delgado",
    audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-victoria-nyc.mp3'),
    fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-victoria-nyc.mp3'),
    carriers: {
      en: {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-victoria-nyc.mp3'),
        audioReady: true,
      },
      'fr-CA': {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-victoria-nyc_fr-ca.mp3'),
        audioReady: true,
      },
    },
    translations: {
      'fr-CA': {
        title: 'Victoria NYC : Chronique de transmission de terrain',
        eyebrow: 'Transmission de terrain de NYC / Lower East Side',
        subject: 'Victoria NYC',
        status: 'Transmission de terrain active',
        transmission: 'Session du dimanche pour l\'anniversaire d\'Alex Orange Drink & Miwi\'s Music Thing',
        summary:
          'Le nœud du 235 Eldridge Street accueille la session de catalogue Miwi\'s Music Thing avec Conor Elmes, Timothy, Heidemann et Baby Delgado, rejoints par Monk Americana pour une collaboration live (non captée sur bande), suivie de la session d\'anniversaire d\'Alex Orange Drink. TogeeVision entrelace la narration studio tech-noir avec des échantillons captés en direct sur le parquet.',
        crewCritique: [
          {
            ...CREW.togee,
            line:
              'Victoria NYC au 235 Eldridge Street. Miwi La Lupa et Conor Elmes animent le catalogue, Heidemann et Baby Delgado bien calés dans le rythme, la session d\'anniversaire d\'Alex Orange Drink bat son plein. Monk Americana était présent et a joué aux côtés de Miwi — un moment souverain en direct en dehors de notre dispositif d\'enregistrement, mais indélébile sur la fréquence. Quand vous refusez la laitue industrielle et transformez votre chambre en serre de micropousses, vous comprenez la radio pirate : quand la chaîne d\'approvisionnement défaille, vous cultivez votre propre fréquence.',
          },
          {
            ...CREW.lex,
            line:
              'Coordonnée de terrain vérifiée au 235 Eldridge Street. Le programme se déroule le dimanche de 16h30 à 19h00, unissant la composition de chansons, la collaboration sur scène et l\'archivage communautaire décentralisé.',
          },
          {
            ...CREW.anansi,
            line:
              'TogeeVision et moi avons réglé les magnétophones de terrain dans la salle pendant que Miwi La Lupa mène la session sous la boule disco. Le set live de Monk Americana avec Miwi reste gravé dans la mémoire de la salle, tandis que notre bande capte la facture acoustique et la célébration d\'anniversaire d\'Alex Orange Drink. Provenance directe, sans intermédiaire.',
          },
          {
            ...CREW.kaelen,
            line:
              'Miwi La Lupa ancre la composition du catalogue soutenu par Conor Elmes et Timothy, Monk Americana se joignant sur scène pendant la session, tandis que TogeeVision injecte des échantillons de terrain remastérisés directement dans la diffusion.',
          },
        ],
        closing:
          'La transmission est en direct sur Eldridge Street. Des racines dans la terre, l\'aiguille dans le sillon, le signal sur les ondes.',
        mediaArchaeology: {
          description:
            'Victoria NYC est un nœud culturel actif au 235 Eldridge Street dans le Lower East Side de Manhattan, accueillant Miwi\'s Music Thing, la session d\'anniversaire d\'Alex Orange Drink, Monk Americana en concert avec Miwi, et TogeeVision & Anansi Byte pour la captation de terrain.',
          highlight:
            'Nœud du Lower East Side : chansons de répertoire, session d\'anniversaire, souveraineté alimentaire des micropousses et captation directe TogeeVision/Anansi.',
        },
      },
    },
    summary:
      "235 Eldridge Street node hosting Miwi's Music Thing catalog session with Conor Elmes, Timothy, Heidemann, and Baby Delgado, joined by Monk Americana for a live collaboration set (uncaptured on tape), followed by Alex Orange Drink's Birthday Sunday Session. TogeeVision weaves noir studio narration with interjected live field samples from the hardwood floor.",
    crewCritique: [
      {
        ...CREW.togee,
        line:
          "Victoria NYC at 235 Eldridge Street. Miwi La Lupa and Conor Elmes run the catalog, Heidemann and Baby Delgado in the pocket, Alex Orange Drink birthday session in full swing. Monk Americana was in the house and played alongside Miwi—a sovereign live moment outside our microphone capture rig, but indelible on the frequency. When you refuse the lettuce on a corporate turkey burger and convert your bedroom into a microgreens grow room, you understand pirate radio: when the food chain fails, you cultivate your own frequency.",
      },
      {
        ...CREW.lex,
        line:
          'Verified field coordinate at 235 Eldridge Street. Program runs 4:30–7:00 PM Sunday, uniting catalog songwriting, live performance collaboration, and decentralized community archiving.',
      },
      {
        ...CREW.anansi,
        line:
          "TogeeVision and I have the field recording rigs dialed into the room while Miwi La Lupa leads the session under the disco ball. Monk Americana's live set with Miwi stays etched in the physical room memory, while our tape captures the acoustic songcraft and the Alex Orange Drink birthday celebration. Direct provenance, zero middleman poison.",
      },
      {
        ...CREW.kaelen,
        line:
          'Miwi La Lupa anchors the catalog songcraft backed by Conor Elmes and Timothy, with Monk Americana joining on stage during the session, while TogeeVision interjects remastered field samples directly into the broadcast transmission.',
      },
    ],
    closing:
      'The transmission is live on Eldridge Street. Roots in the soil, needle in the groove, signal on the air.',
    mediaArchaeology: {
      description: "Victoria NYC is an active cultural node at 235 Eldridge Street in Manhattan's Lower East Side, hosting Miwi's Music Thing, Alex Orange Drink's birthday session, Monk Americana performing live with Miwi (uncaptured on tape), and TogeeVision & Anansi Byte executing the field capture.",
      highlight: 'Lower East Side Node: catalog songs, birthday session, bedroom microgreen food sovereignty, and TogeeVision/Anansi live field capture.'
    },
    provenance: {
      sourceAuthority: 'live_field_capture',
      eventDate: '2026-09-13',
      nodeCoordinate: '235 Eldridge Street, Lower East Side, New York, NY',
      capturedBy: ['TogeeVision', 'Anansi Byte'],
      sourceMaterials: [
        'Victoria NYC Master Audio Recording (02:04:39, 48kHz stereo HE-AAC)',
        'Take 1 Multi-angle Video Capture (PXL_20260914_000705239.TS.mp4)',
        'Take 2 Multi-angle Video Capture (PXL_20260914_002509092.TS.mp4)',
        'Victoria Bar Field Telemetry Stills (Miwi La Lupa, Alex Orange Drink, Room Stage)',
      ],
      rightsStatus: 'sovereign_field_archive',
    },
    acousticTelemetry: {
      duration: '00:02:41.10',
      sampleRate: '44100 Hz',
      channels: 'stereo',
      meanVolumeDb: -14.0,
      peakVolumeDb: -1.0,
      chapters: [
        {
          chapter: 1,
          timecode: '00:00:00',
          title: 'Noir Coordinate & Miwi Opening Songcraft',
          act: 'TogeeVision & Miwi La Lupa',
          energyLevelDb: -14.0,
        },
        {
          chapter: 2,
          timecode: '00:00:50',
          title: 'Ensemble Pocket, Guests & Monk Americana Sovereign Note',
          act: 'Miwi La Lupa, Conor Elmes, Timothy, Heidemann, Baby Delgado',
          energyLevelDb: -13.8,
        },
        {
          chapter: 3,
          timecode: '00:01:25',
          title: 'Alex Orange Drink Birthday Sunday Session',
          act: 'Alex Orange Drink & Node Ensemble',
          energyLevelDb: -13.5,
        },
        {
          chapter: 4,
          timecode: '00:01:58',
          title: 'Food Sovereignty Sign-Off & Room Applause',
          act: 'TogeeVision & Victoria Bar Room',
          energyLevelDb: -14.0,
        },
      ],
    },
    visualAssets: [
      {
        id: 'photo-miwi-stage',
        type: 'image',
        src: '/images/transmissions/victoria-nyc/PXL_20260914_000931174.NIGHT.jpg',
        title: 'Miwi La Lupa Live Acoustic Set',
        caption: 'Miwi La Lupa singing and playing acoustic guitar under the disco ball and exposed brick wall at Victoria NYC.',
      },
      {
        id: 'photo-alex-orange-drink-birthday',
        type: 'image',
        src: '/images/transmissions/victoria-nyc/PXL_20260914_002501028.jpg',
        title: 'Alex Orange Drink Birthday Session',
        caption: 'Alex Orange Drink performing on mic during the Birthday Sunday Session at 235 Eldridge.',
      },
      {
        id: 'photo-victoria-room-telemetry',
        type: 'image',
        src: '/images/transmissions/victoria-nyc/PXL_20260914_002950696.NIGHT.jpg',
        title: 'Victoria Bar Acoustic Geometry',
        caption: 'Room perspective showing the hardwood floors, audience seating, stage monitor wedge, and artwork.',
      },
      {
        id: 'photo-acoustic-archway-songcraft',
        type: 'image',
        src: '/images/transmissions/victoria-nyc/PXL_20260914_004154259.NIGHT.jpg',
        title: 'Acoustic Songcraft at Archway',
        caption: 'Guitarist in black cap and shirt performing acoustic songs in front of the brick archway.',
      },
    ],
  },
  'venue-film-forum-nyc': {
    id: 'venue-film-forum-nyc',
    chronicleId: getTransmissionChronicleId('venue', 'film-forum-nyc'),
    sourceType: 'venue',
    sourceId: 'film-forum-nyc',
    city: 'nyc',
    title: 'Film Forum & The A Train: Once Upon A Time in Harlem Field Transmission',
    eyebrow: 'NYC Field Transmission / Eighth Avenue A Train & West Houston Street',
    subject: 'Film Forum: Once Upon A Time in Harlem (William Greaves, 1972)',
    status: 'Field Transmission Active',
    transmission: "The A Train to Film Forum: Once Upon A Time in Harlem & The Origin of 'Gig'",
    sourcePath: '/passport/transmissions/venue-film-forum-nyc',
    radarPath: '/passport/radar?city=nyc',
    image: '/images/transmissions/nyc/film-forum-once-upon-a-time-in-harlem-marquee.jpg',
    imageAlt: "Film Forum blue neon marquee displaying 'ONCE UPON A TIME IN HARLEM — A HOUSE PARTY FOR THE AGES (NY TIMES)'",
    audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-film-forum-nyc.mp3'),
    fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-film-forum-nyc.mp3'),
    carriers: {
      en: {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-film-forum-nyc.mp3'),
        audioReady: true,
      },
      'fr-CA': {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-film-forum-nyc_fr-ca.mp3'),
        audioReady: true,
      },
    },
    translations: {
      'fr-CA': {
        title: 'Film Forum & Le Métro A : Chronique de transmission Once Upon A Time in Harlem',
        eyebrow: 'Transmission de terrain de NYC / Ligne A de la 8e Avenue et West Houston Street',
        subject: 'Film Forum : Once Upon A Time in Harlem (William Greaves, 1972)',
        status: 'Transmission de terrain active',
        transmission: 'Du métro A au Film Forum : Once Upon A Time in Harlem et l\'origine du mot « Gig »',
        summary:
          'Transmission de terrain à New York retraçant le trajet du métro A depuis la 125e rue à Harlem jusqu\'à West 4th Street et au Film Forum (209 West Houston St) pour la projection du chef-d\'œuvre documentaire de 1972 de William Greaves, Once Upon A Time in Harlem. Archive l\'étymologie fondatrice du Passeport Gig : comment le chef d\'orchestre James Reese Europe a forgé le mot « gig » en fondant le Clef Club en 1910 afin d\'exiger une rémunération juste et une souveraineté artistique pour les musiciens noirs — aux côtés d\'Eubie Blake et Noble Sissle évoquant Shuffle Along (1921), de Bill « Bojangles » Robinson inventant « copacetic » et de Langston Hughes composant « The Weary Blues » à l\'aube sur Lenox Avenue.',
        crewCritique: [
          {
            ...CREW.togee,
            line:
              'À bord du métro A en direction de Brooklyn, de la 125e rue à Harlem jusqu\'à West 4th Street, avant de déboucher sous la marquise au néon bleu du Film Forum au 209 West Houston. À l\'écran : la capsule temporelle de 1972 de William Greaves, Once Upon A Time in Harlem — une fête historique filmée dans la maison de Duke Ellington. Lorsque les aînés de cette salle font remonter le mot « gig » au chef d\'orchestre James Reese Europe refusant les cachets de misère pour les musiciens noirs, toute la mission du Passeport Gig prend son sens.',
          },
          {
            ...CREW.lex,
            line:
              'Coordonnée vérifiée reliant le corridor souterrain du métro A sur la Huitième Avenue (de la 125e rue à West 4th St) et le Film Forum (209 West Houston St, New York, NY). La transmission unit la télémétrie ferroviaire de la MTA au sommet documentaire multi-caméras de William Greaves, ancrant West Houston Street sur le radar du Passeport Gig de New York.',
          },
          {
            ...CREW.anansi,
            line:
              'Écoutez attentivement le fil de provenance préservé sur les bobines de Greaves : le pianiste de quatre-vingt-neuf ans Eubie Blake et Noble Sissle racontant Shuffle Along en 1921, Arna Bontemps se souvenant d\'avoir raccompagné Langston Hughes jusqu\'à l\'aube alors qu\'il composait « The Weary Blues », Bill « Bojangles » Robinson forgeant le mot « copacetic », et l\'origine même du mot « gig ». James Reese Europe a inventé le terme « gig » lorsqu\'il a organisé le Clef Club en 1910 — déclarant aux agents : « Non, vous ne les aurez pas pour soixante-quinze cents par jour, je vais obtenir de l\'argent réel pour vous. » Le mot « gig » est né comme un acte de syndicalisme culturel et de souveraineté musicale noire.',
          },
          {
            ...CREW.kaelen,
            line:
              'Télémétrie acoustique enregistrée en 44.1 kHz stéréo, volume intégré à -14.0 LUFS et crête réelle à -1.0 dBFS. Chaque fois qu\'un artiste ou un témoin scanne un nœud du Passeport Gig aujourd\'hui, il marche directement dans les pas du Clef Club de James Reese Europe — là où un « gig » n\'est jamais une case horaire jetable, mais un registre souverain de valeur culturelle vivante.',
          },
        ],
        closing:
          'Des rails du métro A sur la 125e rue à l\'écran du Film Forum sur West Houston : connaissez la racine du mot « gig », honorez le pacte de James Reese Europe et gardez le signal souverain sur les ondes.',
        mediaArchaeology: {
          description:
            'Reliant le corridor du métro A de Harlem au cinéma indépendant Film Forum (209 West Houston St), cette transmission documente Once Upon A Time in Harlem de William Greaves (tourné en 1972 chez Duke Ellington) et grave l\'origine souveraine du mot « gig » attribuée à James Reese Europe et au Clef Club (1910) au cœur de la mémoire du Passeport Gig.',
          highlight:
            'Du métro A au Film Forum : William Greaves (1972), Eubie Blake et Noble Sissle sur Shuffle Along, et James Reese Europe forgeant le mot « gig » pour défendre la dignité des musiciens noirs.',
        },
      },
    },
    audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'film-forum-nyc'))}`,
    summary:
      "NYC field transmission tracing the Brooklyn-bound A train from 125th Street in Harlem to West 4th Street and Film Forum (209 West Houston St) for William Greaves's long-vaulted 1972 documentary masterpiece Once Upon A Time in Harlem. Captures the foundational Gig Passport origin lore: how bandleader James Reese Europe coined the word 'gig' when organizing the Clef Club in 1910 to demand fair compensation and artistic sovereignty for Black musicians—alongside Eubie Blake and Noble Sissle recalling Shuffle Along (1921), Bill 'Bojangles' Robinson coining 'copacetic,' and Langston Hughes composing 'The Weary Blues' at dawn on Lenox Avenue.",
    crewCritique: [
      {
        ...CREW.togee,
        line:
          "Riding the Brooklyn-bound A train from 125th Street in Harlem down through 59th, 42nd, and 14th to West 4th Street, stepping out under the blue neon marquee of Film Forum at 209 West Houston. On screen is William Greaves's 1972 time capsule Once Upon A Time in Harlem—a house party for the ages filmed inside Duke Ellington's townhouse. When the elders in that room trace the word 'gig' back to bandleader James Reese Europe refusing starvation wages for Black players, the entire mission of the Gig Passport locks into focus.",
      },
      {
        ...CREW.lex,
        line:
          "Verified transit and cinema coordinate linking the Eighth Avenue A Train corridor (125th St to West 4th St) with Film Forum (209 West Houston St, New York, NY). The transmission bridges subterranean MTA rail telemetry with William Greaves's multi-camera archival summit, anchoring West Houston Street on the NYC Gig Passport radar.",
      },
      {
        ...CREW.anansi,
        line:
          "Listen closely to the provenance lore preserved on Greaves's reels: eighty-nine-year-old Eubie Blake and Noble Sissle recounting Shuffle Along in 1921, Arna Bontemps recalling walking Langston Hughes home until dawn as he composed 'The Weary Blues,' Bill 'Bojangles' Robinson coining 'copacetic,' and the origin of the word 'gig' itself. James Reese Europe coined 'gig' when he organized the Clef Club in 1910—telling booking agents, 'No, they'll not get you for no seventy-five cents a day, I'm going to get some money for you.' The word 'gig' was born as collective bargaining and Black musical sovereignty.",
      },
      {
        ...CREW.kaelen,
        line:
          "Acoustic telemetry logged at 44.1kHz stereo, -14.0 LUFS integrated loudness, and -1.0 dBFS true peak. Every time an artist or witness scans a Gig Passport node today, they step directly into James Reese Europe's Clef Club blueprint—where a gig is not a disposable calendar listing, but a sovereign record of living cultural value.",
      },
    ],
    closing:
      "From the 125th Street A train rails to the Film Forum screen on West Houston: know the root of the word 'gig,' honor James Reese Europe's compact, and keep the sovereign signal on the air.",
    mediaArchaeology: {
      description:
        "Linking the Eighth Avenue A Train corridor from Harlem to Film Forum (209 West Houston St), this transmission documents William Greaves's 1972 Harlem Renaissance summit Once Upon A Time in Harlem (filmed at Duke Ellington's townhouse) and records the foundational Gig Passport lore: how bandleader James Reese Europe coined the word 'gig' while organizing the Clef Club to fight for Black musicians' pay and dignity.",
      highlight:
        "A Train to Film Forum: William Greaves's 1972 Harlem summit, Eubie Blake & Noble Sissle on Shuffle Along, and James Reese Europe originating the word 'gig' as sovereign musical labor.",
    },
    provenance: {
      sourceAuthority: 'live_field_capture',
      eventDate: '2026-10-18',
      nodeCoordinate: 'Eighth Avenue A Train Corridor (125th St to West 4th St) & Film Forum (209 West Houston St, New York, NY 10014)',
      capturedBy: ['TogeeVision Field Unit'],
      sourceMaterials: [
        'Brooklyn-Bound A Train Subterranean Transit Capture: 125th St Harlem to West 4th St (Google Drive 1jl6oONwU3asUsJSqDdHSZJdGuelKLUea, 00:27:59.23, 48kHz stereo HE-AAC, SHA-256: 7105904b4440e61ff724a8049dbc39eb52c8eb5828f0ec533d6291e48c478251)',
        "Film Forum Screening Capture: Once Upon A Time in Harlem & James Reese Europe 'Gig' Etymology Lore (Google Drive 1pRUWjN3ez3L833J6EKW6eZ-6vEbcsl8Z, 01:43:29.19, 48kHz stereo HE-AAC, SHA-256: 84e12f1ee3dd419b1635ad46f5424bab3cec7b928ddd946dfe0929fed3b3ba77)",
        "Film Forum Neon Marquee Optical Capture: 'Once Upon A Time in Harlem — A House Party for the Ages' (film-forum-once-upon-a-time-in-harlem-marquee.jpg, SHA-256: f17faf0d00dcfb23e14db7a52cc2087e7b19d274511fde34278fa11927bf2758)",
        'NYC Field Master Audio (transmission_venue-film-forum-nyc.mp3, 00:03:31.04, 44.1kHz stereo, SHA-256: f18c68ad13d9e2e4d65f247b4d33bbf1c4b0e25cc7bf352cfb6beafec4b92abe)',
      ],
      rightsStatus: 'sovereign_field_archive',
    },
    acousticTelemetry: {
      duration: '00:03:31.04',
      sampleRate: '44100 Hz',
      channels: 'stereo',
      meanVolumeDb: -14.0,
      peakVolumeDb: -1.0,
      chapters: [
        {
          chapter: 1,
          timecode: '00:00:00',
          title: 'Black Star Opener & A Train Transit Lock (125th St Harlem to West 4th St)',
          act: 'TogeeVision & MTA Eighth Avenue Line',
          energyLevelDb: -14.0,
        },
        {
          chapter: 2,
          timecode: '00:00:56',
          title: "Film Forum Coordinate & William Greaves's 1972 Co-Creation Manifesto",
          act: 'Lex Gupta & William Greaves Ensemble',
          energyLevelDb: -13.8,
        },
        {
          chapter: 3,
          timecode: '00:01:48',
          title: "James Reese Europe, The Origin of 'Gig' & Eubie Blake Singing Shuffle Along",
          act: 'Anansi Byte & Eubie Blake',
          energyLevelDb: -13.6,
        },
        {
          chapter: 4,
          timecode: '00:02:58',
          title: 'Clef Club Blueprint, Gig Passport Lore & Sovereign Sign-Off',
          act: 'Kaelen Vance & TogeeVision',
          energyLevelDb: -14.0,
        },
      ],
    },
    visualAssets: [
      {
        id: 'photo-film-forum-harlem-marquee',
        type: 'image',
        src: '/images/transmissions/nyc/film-forum-once-upon-a-time-in-harlem-marquee.jpg',
        title: 'Film Forum Neon Marquee: Once Upon A Time in Harlem',
        caption:
          "Blue neon Film Forum marquee at 209 West Houston Street displaying 'ONCE UPON A TIME IN HARLEM — A HOUSE PARTY FOR THE AGES (NY TIMES)' alongside the Andrzej Wajda Retrospective.",
      },
    ],
  },
  'venue-nomaa-gallery-nyc': {
    id: 'venue-nomaa-gallery-nyc',
    chronicleId: getTransmissionChronicleId('venue', 'nomaa-gallery-nyc'),
    sourceType: 'venue',
    sourceId: 'nomaa-gallery-nyc',
    city: 'nyc',
    title: "NoMAA Gallery: 'Dear Earth… Love, Uptown' & Paula Maria Persiani Field Transmission",
    eyebrow: 'NYC Field Transmission / 4140 Broadway (at 176th St) · Washington Heights',
    subject: "NoMAA Gallery: 'Dear Earth… Love, Uptown' & Paula Maria Persiani ('Aquí Me Quedo — Los Cepeda')",
    status: 'Field Transmission Active',
    transmission: "NoMAA Gallery: 'Dear Earth… Love, Uptown' & Paula Maria Persiani's 'Aquí Me Quedo — Los Cepeda'",
    sourcePath: '/passport/transmissions/venue-nomaa-gallery-nyc',
    radarPath: '/passport/radar?city=nyc',
    image: '/images/transmissions/nyc/paula-maria-persiani-aqui-me-quedo-loiza.png',
    imageAlt: "Paula Maria Persiani's mixed-media painting 'Aquí Me Quedo — Los Cepeda' depicting a bright yellow concrete house in Loíza, Puerto Rico surrounded by lush tropical foliage",
    audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-nomaa-gallery-nyc.mp3'),
    fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-nomaa-gallery-nyc.mp3'),
    carriers: {
      en: {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-nomaa-gallery-nyc.mp3'),
        audioReady: true,
      },
      'fr-CA': {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-nomaa-gallery-nyc_fr-ca.mp3'),
        audioReady: true,
      },
    },
    translations: {
      'fr-CA': {
        title: "Galerie NoMAA : Chronique de transmission « Dear Earth… Love, Uptown » et Paula Maria Persiani",
        eyebrow: 'Transmission de terrain de NYC / 4140 Broadway (176e rue) · Washington Heights',
        subject: "Galerie NoMAA : « Dear Earth… Love, Uptown » et Paula Maria Persiani (« Aquí Me Quedo — Los Cepeda »)",
        status: 'Transmission de terrain active',
        transmission: "Galerie NoMAA : « Dear Earth… Love, Uptown » et « Aquí Me Quedo — Los Cepeda » de Paula Maria Persiani",
        summary:
          "Transmission de terrain à la galerie de la Northern Manhattan Arts Alliance (NoMAA, 4140 Broadway à la 176e rue) dans Washington Heights pour le vernissage de « Dear Earth… Love, Uptown » — une lettre visuelle collective à la planète réunissant 29 artistes de West Harlem, Washington Heights et Inwood. Met en lumière l'entretien avec l'artiste nuyorican Paula Maria Persiani autour de son œuvre « Aquí Me Quedo — Los Cepeda » : une maison jaune vif à Loíza (Porto Rico), bastion afro-portoricain fondé après l'émancipation, rendant hommage à la famille Cepeda qui préserve le tambour africain de la bomba et affirmant « Aquí Me Quedo » (« Ici je reste ») face aux sociétés de capital-investissement qui tentent d'acheter l'île.",
        crewCritique: [
          {
            ...CREW.togee,
            line:
              "Au 4140 Broadway et 176e rue, dans la galerie de la Northern Manhattan Arts Alliance pour le vernissage de « Dear Earth… Love, Uptown ». Vingt-neuf artistes de West Harlem, Washington Heights et Inwood transforment la salle en une lettre visuelle adressée à la planète. Devant la maison en béton jaune vif peinte par Paula Maria Persiani au cœur d'un feuillage tropical, l'inscription murale saute aux yeux : « AQUI ME QUEDO — LOS CEPEDA ». Ici je reste. De Loíza à Washington Heights, l'île et la diaspora vibrent sur la même fréquence.",
          },
          {
            ...CREW.lex,
            line:
              "Coordonnée vérifiée à la galerie NoMAA (4140 Broadway, 1er étage, Washington Heights, NYC). Ayant grandi dans le haut de Manhattan et fréquenté Mott Hall, Paula Maria Persiani matérialise le corridor nuyorican et diasporique : elle a filmé la maison jaune vif sur place à Loíza (Porto Rico), a commencé le travail du feuillage sur l'île, puis a rapporté la toile à New York pour assembler l'œuvre finale dans le haut de Manhattan.",
          },
          {
            ...CREW.anansi,
            line:
              "Écoutez la provenance inscrite sur ce mur jaune. Comme l'explique Paula Maria Persiani sur la bande de terrain, ce bâtiment se trouve à Loíza, à Porto Rico — municipalité historique où les personnes affranchies ont bâti leur communauté après l'abolition de l'esclavage. L'inscription « Los Cepeda » honore la légendaire famille Cepeda, vouée à la préservation du tambour africain au sein de la musique et de la culture portoricaines. Et « Aquí Me Quedo » — « Ici je reste » — répond directement aux fonds d'investissement privés et aux grandes sociétés qui cherchent à acheter l'île et à déplacer ses habitants.",
          },
          {
            ...CREW.kaelen,
            line:
              "Télémétrie acoustique enregistrée en 44.1 kHz stéréo, volume intégré à -14.0 LUFS et crête réelle à -1.0 dBFS. Le plan directeur de Persiani démontre pourquoi l'art a besoin d'un ancrage historique et de classe pour toucher la communauté : protéger la Terre, de Loíza à Uptown Manhattan, c'est défendre celles et ceux qui font vivre sa mémoire.",
          },
        ],
        closing:
          "Du 4140 Broadway à Washington Heights jusqu'aux tambours côtiers de Loíza à Porto Rico : honorez Los Cepeda, ancrez l'art dans l'histoire et tenez la ligne — Aquí Me Quedo.",
        mediaArchaeology: {
          description:
            "Reliant le corridor culturel de Washington Heights (NoMAA Gallery, 4140 Broadway) à Loíza, Porto Rico, cette transmission documente le vernissage de « Dear Earth… Love, Uptown » (29 artistes du haut de Manhattan) et l'histoire orale de l'artiste nuyorican Paula Maria Persiani autour de son œuvre « Aquí Me Quedo — Los Cepeda ».",
          highlight:
            "Galerie NoMAA & Paula Maria Persiani : Loíza post-émancipation, le tambour africain de la famille Cepeda, création entre Porto Rico et NYC, et « Aquí Me Quedo » contre la dépossession foncière.",
        },
      },
    },
    audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'nomaa-gallery-nyc'))}`,
    summary:
      "NYC field transmission from the Northern Manhattan Arts Alliance (NoMAA) Gallery at 4140 Broadway (at 176th Street) in Washington Heights for the opening reception of 'Dear Earth… Love, Uptown'—a collective visual letter to the planet by 29 Uptown artists from West Harlem, Washington Heights, and Inwood. Features an in-depth field conversation with Uptown and Nuyorican artist Paula Maria Persiani on her painting of a bright yellow concrete house in Loíza, Puerto Rico bearing the sign 'AQUI ME QUEDO — LOS CEPEDA': honoring the post-emancipation Afro-Puerto Rican stronghold of Loíza, the Cepeda family's preservation of the African bomba drum, cross-corridor creation between Puerto Rico and Uptown NYC, and 'Aquí Me Quedo' ('Here I Stay') as land and class sovereignty against private equity displacement.",
    crewCritique: [
      {
        ...CREW.togee,
        line:
          "Uptown at 4140 Broadway and 176th Street inside the Northern Manhattan Arts Alliance gallery for the opening of 'Dear Earth… Love, Uptown.' Twenty-nine artists from West Harlem, Washington Heights, and Inwood have turned the room into a visual letter to the planet. Standing in front of Paula Maria Persiani's bright yellow concrete house framed by tropical foliage, you read the hand-painted sign on the wall: 'AQUI ME QUEDO — LOS CEPEDA.' Here I Stay. From Loíza to Washington Heights, the diaspora and the island are locked on the same frequency.",
      },
      {
        ...CREW.lex,
        line:
          "Verified gallery coordinate at NoMAA Gallery (4140 Broadway, 1st Floor, Washington Heights, NYC). Raised Uptown and educated at Mott Hall, Paula Maria Persiani maps the Nuyorican and Diasporican circuit in physical form: she filmed the bright yellow concrete house on location in Loíza, Puerto Rico, began crafting the tropical foliage on the island, carried the work back to New York, and assembled the final pieces Uptown.",
      },
      {
        ...CREW.anansi,
        line:
          "Listen to the provenance embedded in that yellow wall. As Paula Maria Persiani explains on the field tape, the building stands in Loíza, Puerto Rico—a historic municipality where formerly enslaved Africans established community after emancipation. The inscription 'Los Cepeda' honors the legendary Cepeda family, whose generational mission is preserving the African drum and bomba rhythm within Puerto Rican music and culture. And 'Aquí Me Quedo'—'Here I Stay'—speaks directly to the present struggle against private equity firms and corporate speculators attempting to buy up the island and displace native Puerto Ricans.",
      },
      {
        ...CREW.kaelen,
        line:
          "Acoustic telemetry logged at 44.1kHz stereo, -14.0 LUFS integrated loudness, and -1.0 dBFS true peak. Persiani's blueprint demonstrates why art requires historical and class context so the community can connect: protecting the Earth from Loíza to Uptown Manhattan means defending the people and rhythms rooted in the land.",
      },
    ],
    closing:
      "From 4140 Broadway in Washington Heights to the coastal drums of Loíza, Puerto Rico: honor Los Cepeda, root the art in history, and hold the line—Aquí Me Quedo.",
    mediaArchaeology: {
      description:
        "Bridging Washington Heights (NoMAA Gallery at 4140 Broadway & 176th St) and Loíza, Puerto Rico, this transmission documents the opening reception of 'Dear Earth… Love, Uptown' (featuring 29 Uptown artists from West Harlem, Washington Heights, and Inwood) and preserves Nuyorican artist Paula Maria Persiani's oral history on 'Aquí Me Quedo — Los Cepeda,' post-emancipation Afro-Puerto Rican memory, and anti-displacement sovereignty.",
      highlight:
        "NoMAA Gallery & Paula Maria Persiani: Loíza post-emancipation history, Los Cepeda's African drum preservation, Puerto Rico-to-NYC foliage collage, and 'Aquí Me Quedo' land sovereignty.",
    },
    provenance: {
      sourceAuthority: 'live_field_capture',
      eventDate: '2026-10-22',
      nodeCoordinate: 'NoMAA Gallery — Northern Manhattan Arts Alliance (4140 Broadway at 176th St, 1st Floor, New York, NY 10033)',
      capturedBy: ['TogeeVision Field Unit'],
      sourceMaterials: [
        "NoMAA Gallery 'Dear Earth… Love, Uptown' Opening Reception & Paula Maria Persiani Field Interview (Google Drive 1HkSQnKESf-vTs0ajIdC7aC_JNXj9dvlv, 00:43:35.81, 48kHz stereo HE-AAC, SHA-256: 182a7c69cc187903685523fe186eeaa087fc75e4bc47974e4c305f949ad34a4e)",
        "NoMAA Gallery 'Dear Earth… Love, Uptown' Reception Optical Capture (nomaa-dear-earth-love-uptown-gallery.jpg, SHA-256: 9f98e508f5a6103eee0cca505b76d2d863c6f3861ad1accca094a12122c971a5)",
        "Paula Maria Persiani 'Aquí Me Quedo — Los Cepeda' (Loíza, Puerto Rico) Artwork Optical Capture (paula-maria-persiani-aqui-me-quedo-loiza.png, SHA-256: cc91d7bdb8ea49cd37b26072560c35d7596bc70c491da84c4ad0dec1554b11fc)",
        'NYC Field Master Audio (transmission_venue-nomaa-gallery-nyc.mp3, 00:03:22.76, 44.1kHz stereo, SHA-256: 957523111a91d28e827c0a4ade3d3b0ea01c2b8ed0bd65161bc95e6ab3ea3e24)',
      ],
      rightsStatus: 'sovereign_field_archive',
    },
    acousticTelemetry: {
      duration: '00:03:22.76',
      sampleRate: '44100 Hz',
      channels: 'stereo',
      meanVolumeDb: -14.0,
      peakVolumeDb: -1.0,
      chapters: [
        {
          chapter: 1,
          timecode: '00:00:00',
          title: "Black Star Opener & NoMAA Gallery 'Dear Earth… Love, Uptown' Lock (4140 Broadway)",
          act: 'TogeeVision & NoMAA Uptown Collective',
          energyLevelDb: -14.0,
        },
        {
          chapter: 2,
          timecode: '00:00:44',
          title: 'Diasporican Identity, Mott Hall Roots & Cross-Corridor Creation (Puerto Rico to NYC)',
          act: 'Lex Gupta & Paula Maria Persiani',
          energyLevelDb: -13.9,
        },
        {
          chapter: 3,
          timecode: '00:01:39',
          title: "Loíza Emancipation History, Los Cepeda's African Drum & 'Aquí Me Quedo' Sovereignty",
          act: 'Anansi Byte & Paula Maria Persiani',
          energyLevelDb: -13.7,
        },
        {
          chapter: 4,
          timecode: '00:02:33',
          title: 'Class & Historical Context in Visual Art, Ecological Solidarity & Sovereign Sign-Off',
          act: 'Kaelen Vance & TogeeVision',
          energyLevelDb: -14.0,
        },
      ],
    },
    visualAssets: [
      {
        id: 'photo-paula-maria-persiani-aqui-me-quedo',
        type: 'image',
        src: '/images/transmissions/nyc/paula-maria-persiani-aqui-me-quedo-loiza.png',
        title: "Paula Maria Persiani: 'Aquí Me Quedo — Los Cepeda' (Loíza, Puerto Rico)",
        caption:
          "Mixed-media painting by Uptown/Nuyorican artist Paula Maria Persiani depicting a bright yellow concrete house in Loíza, Puerto Rico surrounded by tropical foliage made across Puerto Rico and NYC, honoring the Afro-Puerto Rican Cepeda family ('Los Cepeda') and declaring 'Aquí Me Quedo' ('Here I Stay') against displacement.",
      },
      {
        id: 'photo-nomaa-dear-earth-love-uptown-gallery',
        type: 'image',
        src: '/images/transmissions/nyc/nomaa-dear-earth-love-uptown-gallery.jpg',
        title: "NoMAA Gallery Opening Reception: 'Dear Earth… Love, Uptown'",
        caption:
          "Uptown community gathered at the Northern Manhattan Arts Alliance (NoMAA) Gallery at 4140 Broadway (at 176th Street) beneath the exhibition title 'Dear Earth... Love, Uptown,' featuring works by 29 West Harlem, Washington Heights, and Inwood artists.",
      },
    ],
  },
  'venue-pianos-nyc': {
    id: 'venue-pianos-nyc',
    chronicleId: getTransmissionChronicleId('venue', 'pianos-nyc'),
    sourceType: 'venue',
    sourceId: 'pianos-nyc',
    city: 'nyc',
    title: "Pianos NYC: 'I Have A Dream Pop Festival' & The Veldt Field Transmission",
    eyebrow: 'NYC Field Transmission / 158 Ludlow Street · Lower East Side',
    subject: "Pianos NYC: 'I Have A Dream Pop Festival' — The Veldt ('Christine'), Diary, Day & Dream & Darksoft",
    status: 'Field Transmission Active',
    transmission: "Pianos NYC: 'I Have A Dream Pop Festival' — The Veldt ('Christine'), Diary, Day & Dream & Darksoft",
    sourcePath: '/passport/transmissions/venue-pianos-nyc',
    radarPath: '/passport/radar?city=nyc',
    image: '/images/transmissions/nyc/pianos-the-veldt-chavis-vox-phantom.jpg',
    imageAlt: "Daniel Chavis of pioneering Afro-futurist soul-shoegaze band The Veldt performing with his red teardrop Vox Phantom XII twelve-string guitar at Pianos NYC during the 'I Have A Dream Pop Festival'",
    audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-pianos-nyc.mp3'),
    fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-pianos-nyc.mp3'),
    carriers: {
      en: {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-pianos-nyc.mp3'),
        audioReady: true,
      },
      'fr-CA': {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_venue-pianos-nyc_fr-ca.mp3'),
        audioReady: true,
      },
    },
    translations: {
      'fr-CA': {
        title: "Pianos NYC : Chronique de transmission « I Have A Dream Pop Festival » et The Veldt",
        eyebrow: 'Transmission de terrain de NYC / 158 rue Ludlow · Lower East Side',
        subject: "Pianos NYC : « I Have A Dream Pop Festival » — The Veldt (« Christine »), Diary, Day & Dream et Darksoft",
        status: 'Transmission de terrain active',
        transmission: "Pianos NYC : « I Have A Dream Pop Festival » — The Veldt (« Christine »), Diary, Day & Dream et Darksoft",
        summary:
          "Transmission de terrain chez Pianos au 158 rue Ludlow dans le Lower East Side pour la soirée du vendredi du « I Have A Dream Pop Festival » — rassemblement itinérant de shoegaze, dream pop et musique indépendante fondé par les frères jumeaux Daniel et Danny Chavis du groupe pionnier de soul-shoegaze afro-futuriste The Veldt. Documente les concerts du vendredi soir dans le Showroom avec le quatuor dream-pop new-yorkais Diary (fêtant son vinyle rouge sang), le duo Day & Dream, Darksoft venu de Portland dans le Maine avec son album « Everydayness » au symbole solaire de l'analemme, et le mur du son de The Veldt à la douze cordes Vox Phantom ouvrant leur concert avec « Christine ».",
        crewCritique: [
          {
            ...CREW.togee,
            line:
              "Au 158 rue Ludlow dans le Lower East Side, sous l'enseigne historique « NEW & USED BOUGHT & SOLD PIANOS », les bandes de néon rouge au sol traversent la brume indigo pour le « I Have A Dream Pop Festival ». Fondé par les frères jumeaux Daniel et Danny Chavis du groupe pionnier de soul-shoegaze afro-futuriste The Veldt, le festival transforme Pianos en un mur vivant de guitares douze cordes Vox Phantom, de réverbération cathédrale et de solidarité underground.",
          },
          {
            ...CREW.lex,
            line:
              "Coordonnée vérifiée chez Pianos (158 rue Ludlow, Lower East Side, NYC). Entre le Showroom et la mezzanine à l'étage, la soirée du vendredi du festival relie plusieurs corridors en temps réel : Diary célébrant la sortie de son vinyle rouge sang là où le groupe avait joué jadis à l'étage devant deux personnes, le duo synthé-guitare Day & Dream sous une lumière bleu cobalt, et Darksoft descendu de Portland (Maine) via Boston avec son album « Everydayness » orné de l'analemme solaire (« Devil's in the Details », « Endless Day » et « Too Little Too Late »).",
          },
          {
            ...CREW.anansi,
            line:
              "Écoutez la provenance sur la bande de terrain du vendredi soir lorsque Daniel Chavis s'avance au micro, sa douze cordes Vox Phantom rouge en bandoulière. S'adressant aux jeunes groupes de la soirée, il évoque ses années de plongeur à laver la vaisselle pour s'acheter les disques de The Chameleons et The Sound, et son adolescence au lycée en Caroline du Nord avec les cheveux défrisés et du vernis à ongles noir à une époque où personne ne comprenait que de jeunes Noirs jouent du dream pop. Son appel avant d'ouvrir le concert avec « Christine » résume la loi de ce réseau : partager le son, refuser de jouer aux gardiens du temple, prendre soin les uns des autres et ne jamais abandonner.",
          },
          {
            ...CREW.kaelen,
            line:
              "Télémétrie acoustique enregistrée en 44.1 kHz stéréo, volume intégré à -14.0 LUFS et crête réelle à -1.0 dBFS, tissant quatre captations vidéo sur scène de The Veldt avec quatre-vingt-quatre minutes d'enregistrement audio de terrain. Le plan directeur au 158 rue Ludlow prouve que le shoegaze et le dream pop n'ont jamais été une monoculture fermée, mais un circuit d'entraide vivant.",
          },
        ],
        closing:
          "De la Caroline du Nord et de Portland (Maine) jusqu'au 158 rue Ludlow dans le Lower East Side : partagez le son, rejetez les gardiens du temple et laissez porter la réverbération douze cordes.",
        mediaArchaeology: {
          description:
            "Reliant Raleigh (Caroline du Nord), Portland (Maine) et le Lower East Side (Pianos, 158 rue Ludlow), cette transmission documente la soirée du vendredi du « I Have A Dream Pop Festival » fondé par Daniel et Danny Chavis (The Veldt, ouvrant avec « Christine »), avec Diary, Day & Dream et Darksoft.",
          highlight:
            "Pianos NYC & I Have A Dream Pop Festival : soul-shoegaze afro-futuriste de The Veldt (« Christine ») à la Vox Phantom 12 cordes, Diary, Day & Dream, Darksoft et solidarité anti-gardiens du temple.",
        },
      },
    },
    audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'pianos-nyc'))}`,
    summary:
      "NYC field transmission from Pianos at 158 Ludlow Street on the Lower East Side for Friday night of the 'I Have A Dream Pop Festival'—the touring shoegaze, dream-pop, and independent underground convergence founded by identical twin brothers Daniel and Danny Chavis of pioneering Black American soul-shoegaze band The Veldt. Captures Friday night's live Showroom sets by NYC dream-pop quartet Diary (celebrating their blood-red vinyl release), synth-and-guitar duo Day & Dream, Portland, Maine's Darksoft (touring their solar analemma album 'Everydayness'), and The Veldt's headlining wall of sound opening with 'Christine.'",
    crewCritique: [
      {
        ...CREW.togee,
        line:
          "At 158 Ludlow Street on the Lower East Side, underneath the vintage 'NEW & USED BOUGHT & SOLD PIANOS' marquee, red neon floor strips cut through cobalt and indigo haze for the 'I Have A Dream Pop Festival.' Founded by identical twin brothers Daniel and Danny Chavis of pioneering Afro-futurist soul-shoegaze band The Veldt, the festival turns Pianos into a living wall of twelve-string Vox Phantom chime, cathedral reverb, and sovereign underground solidarity.",
      },
      {
        ...CREW.lex,
        line:
          "Verified venue coordinate at Pianos (158 Ludlow Street, Lower East Side, NYC). Across the Showroom and Upstairs Mezzanine, Friday night of the festival connects regional corridors in real time: NYC's Diary celebrating their blood-red vinyl release where they once played upstairs for two people, synth-and-guitar duo Day & Dream under deep cobalt light, and Darksoft driving four hours down from Portland, Maine via Boston with their 'Everydayness' solar analemma record ('Devil's in the Details,' 'Endless Day,' and 'Too Little Too Late').",
      },
      {
        ...CREW.anansi,
        line:
          "Listen to the provenance on the Friday night field tape when Daniel Chavis steps to the microphone with his red Vox Phantom twelve-string strapped high. Looking out at the younger bands in the room, he recalls washing dishes as a kid to buy records by The Chameleons and The Sound, and surviving high school in North Carolina with straight hair and black fingernail polish when nobody knew what to make of Black kids playing dream pop. His charge to the room before launching into their opening track 'Christine' is the core law of this network: share the sound, refuse to be gatekeepers, take care of each other, and never give up.",
      },
      {
        ...CREW.kaelen,
        line:
          "Acoustic telemetry logged at 44.1kHz stereo, -14.0 LUFS integrated loudness, and -1.0 dBFS true peak, weaving four live iPhone 16 stage captures of The Veldt with an 84-minute Pixel Recorder field tape. The blueprint at 158 Ludlow Street proves that shoegaze and dream pop were never a closed monoculture—they are a living, cross-corridor mutual-aid circuit.",
      },
    ],
    closing:
      "From Raleigh and Portland, Maine to 158 Ludlow Street on the Lower East Side: share the sound, reject the gatekeepers, and let the twelve-string reverb carry.",
    mediaArchaeology: {
      description:
        "Bridging Raleigh (North Carolina), Portland (Maine), and the Lower East Side (Pianos at 158 Ludlow Street), this transmission documents Friday night of the 'I Have A Dream Pop Festival' founded by Daniel and Danny Chavis of Afro-futurist soul-shoegaze pioneers The Veldt (opening with 'Christine'), alongside Diary, Day & Dream, and Darksoft.",
      highlight:
        "Pianos NYC & I Have A Dream Pop Festival: The Veldt's Vox Phantom XII soul-shoegaze wall of sound ('Christine'), Diary, Day & Dream, Darksoft, and anti-gatekeeper mutual aid.",
    },
    provenance: {
      sourceAuthority: 'live_field_capture',
      eventDate: '2026-10-23',
      nodeCoordinate: 'Pianos NYC — Showroom & Mezzanine (158 Ludlow Street, Lower East Side, New York, NY 10002)',
      capturedBy: ['TogeeVision Field Unit'],
      sourceMaterials: [
        "Pianos NYC 'I Have A Dream Pop Festival' Friday Showroom Field Audio & Embedded Pixel Transcript (Google Drive 11HZVFEK4AQIfy87CXfjSicNsTTOzTNVw / Oct 23 at 8-24 PM.m4a, 01:24:27.35, 48kHz stereo HE-AAC, SHA-256: b7ad428764a4f92cef666f1f96a222f36497b2974d917c3904f549c7f69f551c)",
        "The Veldt Live Stage Video Capture I at Pianos Showroom (Google Drive 1IYqUx0ieGc4uMQ8mHxjw5ekZ47H_qpZ- / IMG_3199.mov, 00:02:59.05, 1080p24 + 48kHz stereo AAC, SHA-256: cd64d09264da3db30a26036862204d46718a00d5581432ca531ec7516c20c2e4)",
        "The Veldt Live Stage Video Capture II at Pianos Showroom (Google Drive 1R_Y8_3AeAJjiBdz2JlXxmP9lCKRjJNGV / IMG_3200.mov, 00:03:04.34, 1080p24 + 48kHz stereo AAC, SHA-256: 438bf0e6ee2d9245f35adead3d559c89e3852eafffc3b17309ad7c930ceccb25)",
        "The Veldt Live Stage Video Capture III at Pianos Showroom (Google Drive 1_wNrUBSFp18hC3RP8agyxjRw6TmGLeNc / IMG_3201.mov, 00:05:54.45, 1080p24 + 48kHz stereo AAC, SHA-256: e89f30111207001a0f384b04a2cfeecb07fca8a5a19d7e03a8998890e1b5ed85)",
        "The Veldt Live Stage Video Capture IV at Pianos Showroom (Google Drive 1TuBcYHoVYyuhEFtwkgNJ-Oq2G6bq-uUd / IMG_3202.mov, 00:05:28.43, 1080p25 + 48kHz stereo AAC, SHA-256: 2d21cf8f421094d32e5dc76e965089688f05c293402adb1533bd36966ef4dd3b)",
        "Pianos NYC 'I Have A Dream Pop Festival' Optical Archive (7 curated field photographs in /images/transmissions/nyc/pianos-*.jpg from Google Drive Folder 1-HBT8iJ9CW214YYnl-LOeCaBDzbJ3sb1)",
        'NYC Field Master Audio (transmission_venue-pianos-nyc.mp3, 00:03:32.43, 44.1kHz stereo, SHA-256: 193d45913e9e5582704ee645dd5b4831494b447f525b33966c4cc9e423764281)',
      ],
      rightsStatus: 'sovereign_field_archive',
    },
    acousticTelemetry: {
      duration: '00:03:32.43',
      sampleRate: '44100 Hz',
      channels: 'stereo',
      meanVolumeDb: -14.0,
      peakVolumeDb: -1.0,
      chapters: [
        {
          chapter: 1,
          timecode: '00:00:00',
          title: 'Black Star Opener, 158 Ludlow Marquee Lock & The Veldt Vox Phantom Twelve-String Wall of Sound',
          act: 'TogeeVision & The Veldt',
          energyLevelDb: -14.0,
        },
        {
          chapter: 2,
          timecode: '00:00:46',
          title: "Showroom & Mezzanine Convergence: Diary, Day & Dream & Darksoft ('Everydayness')",
          act: 'Lex Gupta & Pianos Friday Lineup',
          energyLevelDb: -13.8,
        },
        {
          chapter: 3,
          timecode: '00:01:47',
          title: "North Carolina Roots, Dishwashing for Post-Punk Records & Opening Track 'Christine'",
          act: 'Anansi Byte & Daniel Chavis (The Veldt)',
          energyLevelDb: -13.6,
        },
        {
          chapter: 4,
          timecode: '00:02:43',
          title: 'Afro-Futurist Soul-Shoegaze Blueprint, Mutual-Aid Circuit & Sovereign Sign-Off',
          act: 'Kaelen Vance & TogeeVision',
          energyLevelDb: -14.0,
        },
      ],
    },
    visualAssets: [
      {
        id: 'photo-pianos-the-veldt-chavis-vox-phantom',
        type: 'image',
        src: '/images/transmissions/nyc/pianos-the-veldt-chavis-vox-phantom.jpg',
        title: 'The Veldt at Pianos Showroom: Daniel Chavis & Red Vox Phantom XII',
        caption:
          "Daniel Chavis of pioneering Afro-futurist soul-shoegaze band The Veldt performing under cobalt stage light at Pianos (158 Ludlow Street) with his signature red teardrop Vox Phantom XII twelve-string guitar during the 'I Have A Dream Pop Festival.'",
      },
      {
        id: 'photo-pianos-ludlow-marquee-dream-pop',
        type: 'image',
        src: '/images/transmissions/nyc/pianos-ludlow-marquee-dream-pop.jpg',
        title: 'Pianos Storefront Marquee at 158 Ludlow Street',
        caption:
          "The historic white storefront sign at 158 Ludlow Street on the Lower East Side ('NEW & USED BOUGHT & SOLD — PIANOS — INCREDIBLE PRICES') glowing above the Showroom entrance during the 'I Have A Dream Pop Festival.'",
      },
      {
        id: 'photo-pianos-dream-pop-festival-poster-2026-10-24',
        type: 'image',
        src: '/images/transmissions/nyc/pianos-i-have-a-dream-pop-festival-poster-2026-10-24.png',
        title: "I Have A Dream Pop Festival (Sat. 10/24/26 NYC) Official Poster",
        caption:
          "Official Saturday 10/24/26 NYC poster for the 'I Have A Dream Pop Festival' at Pianos (158 Ludlow St., L.E.S., NYC) founded by Daniel and Danny Chavis of The Veldt—featuring The Veldt, Burnt Sugar (NYC), Honeychild Coleman's DEM (NYC), Spirit Hotel, Mahogany, Faith, and more.",
      },
      {
        id: 'photo-pianos-showroom-neon-floor',
        type: 'image',
        src: '/images/transmissions/nyc/pianos-showroom-neon-floor.jpg',
        title: 'Pianos Showroom: Red Neon Floor Strips & Disco Ball',
        caption:
          "Inside the Pianos Showroom at 158 Ludlow Street, where parallel red neon floor strips lead past the arched bar toward the stage during the 'I Have A Dream Pop Festival.'",
      },
      {
        id: 'photo-pianos-the-veldt-full-band-live',
        type: 'image',
        src: '/images/transmissions/nyc/pianos-the-veldt-full-band-live.jpg',
        title: 'The Veldt Full Band Wall of Sound at Pianos NYC',
        caption:
          'Twin brothers Daniel and Danny Chavis and the full five-piece lineup of The Veldt locked into a wall of shoegaze and soul guitar reverb in front of a packed Pianos Showroom crowd.',
      },
      {
        id: 'photo-pianos-diary-live-showroom',
        type: 'image',
        src: '/images/transmissions/nyc/pianos-diary-live-showroom.jpg',
        title: 'Diary Live at Pianos Showroom (Blood-Red Vinyl Set)',
        caption:
          'NYC dream-pop quartet Diary performing on the Pianos Showroom stage under magenta and violet lights, celebrating their new blood-red vinyl record.',
      },
      {
        id: 'photo-pianos-day-and-dream-live',
        type: 'image',
        src: '/images/transmissions/nyc/pianos-day-and-dream-live.jpg',
        title: 'Day & Dream Live at Pianos NYC',
        caption:
          "Synth-and-guitar dream-pop duo Day & Dream bathed in deep blue Showroom light at Pianos during Friday night of the 'I Have A Dream Pop Festival.'",
      },
      {
        id: 'photo-pianos-darksoft-analemma-merch',
        type: 'image',
        src: '/images/transmissions/nyc/pianos-darksoft-analemma-merch.jpg',
        title: "Darksoft 'Everydayness' Solar Analemma Emblem",
        caption:
          "Portland, Maine dream-pop artist Darksoft's t-shirt display at Pianos featuring the figure-eight solar analemma infinity loop from the album 'Everydayness.'",
      },
    ],
    archiveItems: [
      {
        id: 'pianos-the-veldt-friday-showroom-video-2026-10-23',
        title: "The Veldt Live at Pianos NYC Showroom — 'I Have A Dream Pop Festival' (Friday Night Capture)",
        date: 'October 23, 2026 · Pianos NYC Showroom',
        duration: 'Live Field Video',
        visibility: 'public',
        type: 'video',
        url: 'https://drive.google.com/file/d/1Kp1Aqz6k-DKRYmi917TsQRHjVGd3-kL7/view?usp=drive_link',
        accessUrl: 'https://drive.google.com/file/d/1Kp1Aqz6k-DKRYmi917TsQRHjVGd3-kL7/view?usp=drive_link',
        thumbnail: '/images/transmissions/nyc/pianos-the-veldt-full-band-live.jpg',
        caption:
          "Friday night Showroom live video capture of Afro-futurist soul-shoegaze pioneers The Veldt (Daniel & Danny Chavis and five-piece band) performing at Pianos NYC (158 Ludlow Street) during the 'I Have A Dream Pop Festival.'",
      },
      {
        id: 'pianos-honeychild-dem-video-clip-1-2026-10-24',
        title: "HoneyChild's DEM w/ Monk Americana — Live at Pianos NYC Upstairs Lounge (Saturday Night Clip I)",
        date: 'October 24, 2026 · Pianos NYC Upstairs Lounge',
        duration: 'Live Stage Video',
        visibility: 'public',
        type: 'video',
        url: 'https://docs.google.com/videos/d/1qHQEHzuGGaaraJjhmIyjaZRXo0hxPN8xpSgkl3_zEOI/play?usp=sharing',
        accessUrl: 'https://docs.google.com/videos/d/1qHQEHzuGGaaraJjhmIyjaZRXo0hxPN8xpSgkl3_zEOI/play?usp=sharing',
        thumbnail: '/images/transmissions/nyc/pianos-honeychild-dem-monk-stage-red.jpg',
        caption:
          "Saturday night stage video capture (IMG_3207) of Honeychild Coleman (HoneyChild's DEM) and Monk Americana performing at Pianos NYC Upstairs Lounge (158 Ludlow Street) during the 'I Have A Dream Pop Festival.'",
      },
      {
        id: 'pianos-honeychild-dem-video-clip-2-2026-10-24',
        title: "HoneyChild's DEM w/ Monk Americana & Jennie Oliver-Cretara — 'The Salt' Live at Pianos NYC (Saturday Night Clip II)",
        date: 'October 24, 2026 · Pianos NYC Upstairs Lounge',
        duration: '4K Live Stage Video',
        visibility: 'public',
        type: 'video',
        url: 'https://docs.google.com/videos/d/1DxnuNZmyvx6XaJJEPIGwuq_Q4UY5-RKzt6WBxmgMyvA/play?usp=sharing',
        accessUrl: 'https://docs.google.com/videos/d/1DxnuNZmyvx6XaJJEPIGwuq_Q4UY5-RKzt6WBxmgMyvA/play?usp=sharing',
        thumbnail: '/images/transmissions/nyc/pianos-honeychild-dem-the-salt-viola.jpg',
        caption:
          "Saturday night 4K stage video capture (PXL_20261025_003408137) of HoneyChild's DEM with Monk Americana and special guest Jennie Oliver-Cretara on viola performing 'The Salt' at Pianos NYC Upstairs Lounge.",
      },
      {
        id: 'pianos-honeychild-dem-video-clip-3-2026-10-24',
        title: "HoneyChild's DEM w/ Monk Americana — Live at Pianos NYC Upstairs Lounge (Saturday Night Clip III)",
        date: 'October 24, 2026 · Pianos NYC Upstairs Lounge',
        duration: 'Live Stage Video',
        visibility: 'public',
        type: 'video',
        url: 'https://docs.google.com/videos/d/1CYlQdBHyrkXJM_UVtJpzTLSPyVUCbCotMknqgZW8aFQ/play?usp=sharing',
        accessUrl: 'https://docs.google.com/videos/d/1CYlQdBHyrkXJM_UVtJpzTLSPyVUCbCotMknqgZW8aFQ/play?usp=sharing',
        thumbnail: '/images/transmissions/nyc/pianos-honeychild-dem-monk-disco-ball.jpg',
        caption:
          "Saturday night stage video capture (IMG_3208) of Honeychild Coleman (HoneyChild's DEM) and Monk Americana under crimson neon and mirror-ball light at Pianos NYC (158 Ludlow Street).",
      },
      {
        id: 'pianos-honeychild-dem-video-clip-4-2026-10-24',
        title: "HoneyChild's DEM w/ Monk Americana — Live at Pianos NYC Upstairs Lounge (Saturday Night Clip IV)",
        date: 'October 24, 2026 · Pianos NYC Upstairs Lounge',
        duration: 'Live Stage Video',
        visibility: 'public',
        type: 'video',
        url: 'https://docs.google.com/videos/d/13VF0mI1ljMCaahOQ9XnE4KgscByzYxdPAi0pFqwI9Ok/play?usp=sharing',
        accessUrl: 'https://docs.google.com/videos/d/13VF0mI1ljMCaahOQ9XnE4KgscByzYxdPAi0pFqwI9Ok/play?usp=sharing',
        thumbnail: '/images/transmissions/nyc/pianos-honeychild-dem-quartet-wide.jpg',
        caption:
          "Saturday night stage video capture (IMG_3209) of HoneyChild's DEM and Monk Americana locked into a continuous dub-shoegaze groove at Pianos NYC Upstairs Lounge.",
      },
      {
        id: 'pianos-apollo-heights-dakini-vimeo-8833617',
        title: 'Dakini — Apollo Heights (The Veldt) Live at Ella, East Village NY (Hi-8 Archive)',
        date: 'East Village Hi-8 Capture · Ella Lounge',
        duration: '03:55 Hi-8 Video',
        visibility: 'public',
        type: 'video',
        url: 'https://vimeo.com/8833617',
        accessUrl: 'https://vimeo.com/8833617',
        thumbnail: 'https://i.vimeocdn.com/video/42220179-36894c38ce0fe3ce06b21b54cad69ce524109cf44ec35c2e49621e2bbb486e13-d_640?region=us',
        caption: "Archival Digital Hi-8 capture by Delano Franklin of Apollo Heights (twin brothers Daniel and Danny Chavis of The Veldt) performing 'Dakini' from 'White Music for Black People' live at Ella in the East Village, NY.",
      },
      {
        id: 'pianos-apollo-heights-after-taste-vimeo-8833044',
        title: 'After Taste — Apollo Heights (The Veldt) Live at Ella, East Village NY (Hi-8 Archive)',
        date: 'East Village Hi-8 Capture · Ella Lounge',
        duration: '05:21 Hi-8 Video',
        visibility: 'public',
        type: 'video',
        url: 'https://vimeo.com/8833044',
        accessUrl: 'https://vimeo.com/8833044',
        thumbnail: 'https://i.vimeocdn.com/video/42216601-6917dc3a4f9e596c1fa8c8a3dc7480e0b9b055290d3f90f643f4daf507332f01-d_640?region=us',
        caption: "Archival Digital Hi-8 capture by Delano Franklin of Apollo Heights (twin brothers Daniel and Danny Chavis of The Veldt) performing 'After Taste' live at Ella in the East Village, NY.",
      },
      {
        id: 'pianos-dream-pop-festival-poster-2026-10-24',
        title: "I Have A Dream Pop Festival (Sat. 10/24/26 NYC) Official Poster — Pianos NYC",
        date: 'October 24, 2026 · Pianos NYC',
        duration: 'Festival Poster',
        visibility: 'public',
        type: 'image',
        url: '/images/transmissions/nyc/pianos-i-have-a-dream-pop-festival-poster-2026-10-24.png',
        accessUrl: '/images/transmissions/nyc/pianos-i-have-a-dream-pop-festival-poster-2026-10-24.png',
        caption: "Official Saturday 10/24/26 NYC poster for the 'I Have A Dream Pop Festival' at Pianos (158 Ludlow St., L.E.S.) featuring The Veldt, Burnt Sugar (NYC), Honeychild Coleman's DEM (NYC), Spirit Hotel, Mahogany, Faith, and more.",
      },
    ],
  },
  'venue-the-nick-birmingham': {
    id: 'venue-the-nick-birmingham',
    chronicleId: getTransmissionChronicleId('venue', 'the-nick-birmingham'),
    sourceType: 'venue',
    sourceId: 'the-nick-birmingham',
    city: 'birmingham',
    title: 'The Nick Birmingham: Transmission Chronicle',
    eyebrow: 'Birmingham Venue Transmission No. 1',
    subject: 'The Nick Birmingham',
    status: 'Anticipation Node',
    transmission: 'The Nick / Independent-Room Circuit',
    sourcePath: '/passport/transmissions/venue-the-nick-birmingham',
    radarPath: '/passport/radar?city=birmingham',
    audioUrl: null,
    audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'the-nick-birmingham'))}`,
    summary: 'Birmingham independent-room anchor for an upcoming acoustic stomp set.',
    crewCritique: [
      {
        ...CREW.togee,
        line: 'The Nick anchors the Birmingham independent room circuit: punk heritage and folk starlight.',
      },
    ],
    closing: 'The Birmingham frequency is locked.',
    mediaArchaeology: {
      description: 'The Nick is an independent live music venue in Birmingham, Alabama.',
      highlight: 'Birmingham Independent Room Circuit.'
    },
  },
  'venue-dial-back-sound': {
    id: 'venue-dial-back-sound',
    chronicleId: getTransmissionChronicleId('venue', 'dial-back-sound'),
    sourceType: 'venue',
    sourceId: 'dial-back-sound',
    city: 'birmingham',
    title: 'Dial Back Sound: Transmission Chronicle',
    eyebrow: 'Water Valley Studio Transmission',
    subject: 'Dial Back Sound',
    status: 'Anticipation Node',
    transmission: 'Dial Back Sound / Studio Signal',
    sourcePath: '/passport/transmissions/venue-dial-back-sound',
    radarPath: '/passport/radar?city=birmingham',
    audioUrl: null,
    audioMetaPath: `/api/chronicle/audio-meta/${encodeURIComponent(getTransmissionChronicleId('venue', 'dial-back-sound'))}`,
    summary: "Water Valley recording and showcase coordinate for Taylor Hollingsworth's folk n' roll signal.",
    crewCritique: [
      {
        ...CREW.togee,
        line: 'Dial Back Sound serves as the recording and showcase coordinate for regional roots music.',
      },
    ],
    closing: 'Studio transmission active.',
    mediaArchaeology: {
      description: 'Water Valley recording and showcase coordinate.',
      highlight: 'Water Valley Studio & Showcase Node.'
    },
  },
  'venue-parcel-5': PARCEL_5_TRANSMISSION,
};

const VENUE_TRANSMISSION_ALIASES = {
  'kismat-washington-heights': VENUE_TRANSMISSION_CHRONICLES['venue-kismat-washington-heights'],
  'kismat': VENUE_TRANSMISSION_CHRONICLES['venue-kismat-washington-heights'],
  'kismat-lounge': VENUE_TRANSMISSION_CHRONICLES['venue-kismat-washington-heights'],
  'venue-kismat-lounge': VENUE_TRANSMISSION_CHRONICLES['venue-kismat-washington-heights'],
  'venue-kismat': VENUE_TRANSMISSION_CHRONICLES['venue-kismat-washington-heights'],
  'pianos-nyc': VENUE_TRANSMISSION_CHRONICLES['venue-pianos-nyc'],
  'pianos': VENUE_TRANSMISSION_CHRONICLES['venue-pianos-nyc'],
  'venue-pianos': VENUE_TRANSMISSION_CHRONICLES['venue-pianos-nyc'],
  'pianos-on-ludlow': VENUE_TRANSMISSION_CHRONICLES['venue-pianos-nyc'],
  'i-have-a-dream-pop-festival': VENUE_TRANSMISSION_CHRONICLES['venue-pianos-nyc'],
  'i-have-a-dream-pop': VENUE_TRANSMISSION_CHRONICLES['venue-pianos-nyc'],
  'dream-pop-festival': VENUE_TRANSMISSION_CHRONICLES['venue-pianos-nyc'],
  'festival-i-have-a-dream-pop-nyc': VENUE_TRANSMISSION_CHRONICLES['venue-pianos-nyc'],
  'the-veldt': VENUE_TRANSMISSION_CHRONICLES['venue-pianos-nyc'],
  'nomaa-gallery-nyc': VENUE_TRANSMISSION_CHRONICLES['venue-nomaa-gallery-nyc'],
  'nomaa-gallery': VENUE_TRANSMISSION_CHRONICLES['venue-nomaa-gallery-nyc'],
  'venue-nomaa-gallery': VENUE_TRANSMISSION_CHRONICLES['venue-nomaa-gallery-nyc'],
  'venue-nomaa': VENUE_TRANSMISSION_CHRONICLES['venue-nomaa-gallery-nyc'],
  'nomaa-nyc': VENUE_TRANSMISSION_CHRONICLES['venue-nomaa-gallery-nyc'],
  'nomaa': VENUE_TRANSMISSION_CHRONICLES['venue-nomaa-gallery-nyc'],
  'dear-earth-love-uptown': VENUE_TRANSMISSION_CHRONICLES['venue-nomaa-gallery-nyc'],
  'aqui-me-quedo': VENUE_TRANSMISSION_CHRONICLES['venue-nomaa-gallery-nyc'],
  'los-cepeda': VENUE_TRANSMISSION_CHRONICLES['venue-nomaa-gallery-nyc'],
  'film-forum-nyc': VENUE_TRANSMISSION_CHRONICLES['venue-film-forum-nyc'],
  'film-forum': VENUE_TRANSMISSION_CHRONICLES['venue-film-forum-nyc'],
  'venue-film-forum': VENUE_TRANSMISSION_CHRONICLES['venue-film-forum-nyc'],
  'a-train-film-forum': VENUE_TRANSMISSION_CHRONICLES['venue-film-forum-nyc'],
  'the-a-train': VENUE_TRANSMISSION_CHRONICLES['venue-film-forum-nyc'],
  'once-upon-a-time-in-harlem': VENUE_TRANSMISSION_CHRONICLES['venue-film-forum-nyc'],
  'festival-rochester-fringe-2026': {
    ...PARCEL_5_TRANSMISSION,
    id: 'festival-rochester-fringe-2026',
    title: 'Rochester Fringe Festival: Field Transmission Chronicle',
  },
  'venue-the-church-rochester': PARCEL_5_TRANSMISSION,
  'the-church-rochester': PARCEL_5_TRANSMISSION,
  'the-church': PARCEL_5_TRANSMISSION,
  'hallock-lounge': PARCEL_5_TRANSMISSION,
  'parcel-5': PARCEL_5_TRANSMISSION,
  'venue-turbo-haus': TURBO_HAUS_MONTREAL_TRANSMISSION,
  'turbo-haus-montreal': TURBO_HAUS_MONTREAL_TRANSMISSION,
  'turbo-haus': TURBO_HAUS_MONTREAL_TRANSMISSION,
  'bily-kun': TURBO_HAUS_MONTREAL_TRANSMISSION,
  'bily-kun-montreal': TURBO_HAUS_MONTREAL_TRANSMISSION,
  'venue-lescogriffe': LESCOGRIFFE_MONTREAL_TRANSMISSION,
  'lescogriffe-montreal': LESCOGRIFFE_MONTREAL_TRANSMISSION,
  'lescogriffe': LESCOGRIFFE_MONTREAL_TRANSMISSION,
  'bar-lescogriffe': LESCOGRIFFE_MONTREAL_TRANSMISSION,
  'bar-lescogriffe-montreal': LESCOGRIFFE_MONTREAL_TRANSMISSION,
  'venue-lesco': LESCOGRIFFE_MONTREAL_TRANSMISSION,
  'festival-mapp-mtl-2026': {
    ...MINUTE_MAPP_MONTREAL_TRANSMISSION,
    id: 'festival-mapp-mtl-2026',
    title: 'MINUTE_MAPP 2026: Montreal Field Transmission',
  },
  'venue-minute-mapp': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'festival-minute-mapp-montreal': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'festival-minute-mapp': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'festival-mapp-mtl-montreal': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'festival-mapp-mtl': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'minute-mapp-montreal': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'minute-mapp': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'mapp-mtl': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'mapp-festival': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'million-carpets-and-tiles': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'van-horne-skatepark': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'van-horne': MINUTE_MAPP_MONTREAL_TRANSMISSION,
};

const FIELD_TRANSMISSION_CHRONICLES = {
  'transmission_field-the-dog-still-listening': {
    id: 'field-the-dog-still-listening',
    chronicleId: 'transmission_field-the-dog-still-listening',
    sourceType: 'field',
    pillar: 'field',
    isPrimeAnchor: true,
    primeBadge: 'Prime Laurentian Field Note',
    counterweightNodeId: 'node-harlem-505-w147-studio',
    sourceId: 'the-dog-still-listening',
    city: 'jersey',
    title: 'The Dog Still Listening: Transmission Chronicle',
    eyebrow: 'Prime Laurentian Field Note · Field Transmission No. 005',
    subject: 'The Dog Still Listening',
    status: 'Active Roadside Signal · Northern Anchor',
    transmission: 'Laurentian & Jersey Roadside Field Note',
    sourcePath: '/fields/the-dog-still-listening',
    radarPath: '/passport/radar?city=jersey',
    audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_field-the-dog-still-listening.mp3'),
    fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_field-the-dog-still-listening.mp3'),
    audioMetaPath: '/api/chronicle/audio-meta/transmission_field-the-dog-still-listening',
    soundCue:
      'The warm 78 RPM shellac crackle of a St-Henri gramophone horn dissolving into open New Jersey-to-Laurentian highway wind, answered across the corridor by the metallic flip-snap of Eugene L. Fitch’s 1902 brass Plato Clock plates at Coordinate 505.',
    openingNarrative:
      'Prime Laurentian Field Note locked at Transmission No. 005: The Dog Still Listening. Out along a two-lane stretch of New Jersey road heading north toward the Hudson and the Laurentian borderlands, where old roadside Americana still hums beneath the power lines, a sculpted dog painted Quaker-blue and drifted over with white summer clouds and peace signs sits by the shoulder with its head cocked toward the sky. In the architecture of our network, this roadside dog is the moving ear of the corridor—the northern counterweight to our fixed engine room inside the 1901 invisible territory at Coordinate 505 in Harlem.',
    about:
      'Every living transmission circuit needs two anchors: a clock to mark the moment, and an ear to witness the echo. Down at 503–505 West 147th Street in Harlem—inside the thirty-year invisible territory created by the 1901 construction confusion—railroad bridge builder Levi Penfield Treadwell drafted stone rail spans across space at No. 503 while clockmaker Eugene L. Fitch tilted a brass spindle at No. 505 so gravity would drop numbered plates of witnessed time. Up along the open road stretching from New Jersey through upstate New York to the Laurentian shield and Montréal, The Dog Still Listening completes that instrument.',
    workshopSignificance:
      'In 1898—three years before Fitch sat under gaslight in Harlem filing his Plato Clock patent—Francis Barraud painted his terrier Nipper staring into a phonograph horn for His Master’s Voice, an emblem stamped onto millions of 78s from Camden, New Jersey to Montréal’s historic St-Henri Berliner and RCA Victor factory. Just as Fitch refused to turn his falling clock plates into billboards and submerged his clockwork inside a water-filled glass dome, the folk artist who painted clouds and peace signs over this roadside dog stripped away the master’s phonograph—tuning Nipper to the 1854 Frequency in an age of synthetic AI voices.',
    carriers: {
      en: {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_field-the-dog-still-listening.mp3'),
        audioReady: true,
      },
      'fr-CA': {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_field-the-dog-still-listening_fr-ca.mp3'),
        audioReady: true,
      },
    },
    translations: {
      'fr-CA': {
        primeBadge: 'Note de terrain laurentienne principale',
        title: 'Le chien qui écoute toujours : Chronique de transmission',
        eyebrow: 'Note de terrain laurentienne principale · Transmission n° 005',
        subject: 'Le chien qui écoute toujours',
        status: 'Signal routier actif · Ancre nord',
        transmission: 'Note de terrain laurentienne et du bord de route du New Jersey',
        soundCue:
          'Le craquement chaleureux d’un sillon 78 tours en gomme-laque de Saint-Henri se fondant dans le vent de la route entre le New Jersey et le bouclier laurentien, auquel répond le claquement métallique des plaques de laiton de l’horloge Plato de 1902 à la Coordonnée 505.',
        openingNarrative:
          'Note de terrain laurentienne principale verrouillée à la Transmission n° 005 : Le chien qui écoute toujours. Le long d’une route à deux voies du New Jersey filant vers le nord en direction de l’Hudson et des confins laurentiens, un chien sculpté peint en bleu quaker, parcouru de nuages blancs d’été et de signes de paix, veille sur l’accotement. Dans l’architecture de notre réseau, ce chien du bord de route est l’oreille en mouvement du corridor — le contrepoids nordique de notre salle des machines fixe ancrée dans le territoire invisible de 1901 à la Coordonnée 505 à Harlem.',
        about:
          'Tout circuit de transmission vivant a besoin de deux ancres : une horloge pour marquer l’instant, et une oreille pour témoigner de l’écho. Au 503–505 West 147th Street à Harlem — au cœur du territoire invisible de trente ans créé par la confusion de construction de 1901 — le bâtisseur de ponts ferroviaires Levi Penfield Treadwell traçait les rails au n° 503 tandis que l’horloger Eugene L. Fitch faisait tomber des plaques de laiton de temps témoigné au n° 505. Sur la route ouverte reliant le New Jersey au bouclier laurentien, Le chien qui écoute toujours complète cet instrument.',
        workshopSignificance:
          'En 1898 — trois ans avant le brevet de la Plato Clock de Fitch à Harlem — Francis Barraud peignait Nipper fixant le pavillon de La Voix de son Maître, emblème pressé de Camden jusqu’à l’usine historique Berliner et RCA Victor de Saint-Henri à Montréal. Tout comme Fitch a refusé de vendre ses plaques d’horloge à la réclame et a plongé son mécanisme dans un dôme d’eau, la main qui a peint des nuages et des signes de paix sur ce chien du bord de route a effacé le phonographe du maître pour l’accorder sur la Fréquence 1854.',
        summary:
          'Sur la route reliant le New Jersey au bouclier laurentien, un chien peint en bleu quaker couvert de nuages et de signes de paix répond à la salle des machines du territoire invisible de 1901 à Harlem (Coordonnée 505) et détourne Nipper — l’emblème de l’usine Berliner/RCA Victor de Saint-Henri à Montréal — de La Voix de son Maître vers l’écoute souveraine à l’ère des voix synthétiques d’IA.',
        crewCritique: [
          {
            ...CREW.togee,
            line:
              'Un chien sculpté peint en bleu quaker, parcouru de nuages blancs et de signes de paix au bord de la route : il est l’oreille en mouvement du corridor, le contrepoids nordique de notre salle des machines à la Coordonnée 505 à Harlem. Il se dresse là où s’arrête la voix du maître et où commence le signal de la communauté.',
          },
          {
            ...CREW.lex,
            line:
              'Tout circuit vivant a besoin d’une horloge et d’une oreille. Dans le territoire invisible de 1901 au 503–505 West 147th Street à Harlem, Levi Penfield Treadwell a tracé les ponts ferroviaires et Eugene L. Fitch a bâti l’horloge à plaques de laiton ; sur la route vers le bouclier laurentien, ce chien bleu est le récepteur qui capte le signal dans la nuit.',
          },
          {
            ...CREW.anansi,
            line:
              'En 1898, Barraud peignait Nipper guettant la voix de son maître défunt — une image transformée en empire de Camden jusqu’à l’usine Berliner et RCA Victor de Saint-Henri à Montréal. Tout comme Fitch a refusé la réclame sur ses plaques pour plonger son horloge dans un dôme d’eau, les nuages peints sur ce chien du bord de route effacent le phonographe du maître et l’accordent sur la Fréquence 1854.',
          },
          {
            ...CREW.kaelen,
            line:
              'Écoutez les deux fréquences se rejoindre : le bourdonnement à 60 cycles et le grondement du métro A sous la 147e rue pendant le pressage des cartes 300 g/m², face au craquement d’une aiguille 78 tours qui se fond dans le vent de la route vers la rivière Tomifobia. Le nœud de Harlem construit l’horloge ; la note de terrain laurentienne écoute l’obscurité entre les battements.',
          },
        ],
        closing:
          'Du territoire invisible de 1901 à la Coordonnée 505 à Harlem jusqu’aux murs de brique de Saint-Henri à Montréal : refusez le pavillon du maître et gardez la lentille d’eau claire. Branchez-vous sur le chien qui écoute toujours.',
        mediaArchaeology: {
          description:
            'Reliant le bord de route du New Jersey, le territoire invisible de 1901 au 503–505 West 147th Street à Harlem (horloge Plato d’Eugene L. Fitch et ponts ferroviaires de Levi Penfield Treadwell) et l’usine historique de gramophones Berliner / RCA Victor de Saint-Henri à Montréal, cette note de terrain applique le Filtre de Flaque de 1854 au mythe corporatif « La Voix de son Maître ».',
          highlight:
            'L’Horloge et l’Oreille : La Note de terrain laurentienne principale (Ancre nord) répond aux plaques de laiton de la Coordonnée 505 à Harlem et réinvente Nipper en écoute souveraine.',
        },
        acousticTelemetry: {
          duration: '00:05:15.04',
          sampleRate: '44100 Hz',
          channels: 'stereo',
          meanVolumeDb: -14.0,
          peakVolumeDb: -1.0,
          chapters: [
            {
              chapter: 1,
              timecode: '00:00:00',
              title: 'La sentinelle bleu quaker et l’oreille en mouvement du corridor',
              act: 'TogeeVision et sillon 78 tours',
              energyLevelDb: -14.0,
            },
            {
              chapter: 2,
              timecode: '00:01:07',
              title: 'L’horloge et l’oreille (du territoire invisible de 1901 à Harlem au bouclier laurentien)',
              act: 'Lex Gupta, Fitch et Treadwell',
              energyLevelDb: -13.9,
            },
            {
              chapter: 3,
              timecode: '00:02:21',
              title: 'De Nipper (1898) et l’usine Berliner de Saint-Henri au dôme d’eau de Fitch',
              act: 'Anansi Byte et le Filtre de Flaque 1854',
              energyLevelDb: -13.7,
            },
            {
              chapter: 4,
              timecode: '00:03:56',
              title: 'Écouter l’obscurité entre les battements et clôture sur la Fréquence 1854',
              act: 'Kaelen Vance et TogeeVision',
              energyLevelDb: -14.0,
            },
          ],
        },
        visualAssets: [
          {
            id: 'photo-dog-still-listening-nipper-dialogue',
            type: 'image',
            src: '/images/dog_listening_combined.png',
            title: 'Le chien qui écoute toujours // Dialogue avec Nipper (1898–2026)',
            caption:
              'Le chien bleu quaker peint de nuages et de signes de paix au bord de la route du New Jersey en dialogue avec le Nipper de 1898 de Francis Barraud et l’héritage Berliner / RCA Victor de Saint-Henri à Montréal.',
          },
        ],
      },
    },
    summary:
      'On the road from New Jersey toward the Laurentian shield, a Quaker-blue roadside dog painted with summer clouds and peace signs answers the fixed engine room inside Harlem’s 1901 invisible territory at Coordinate 505—reclaiming Nipper, the emblem of His Master’s Voice and Montréal’s historic St-Henri Berliner / RCA Victor gramophone factory, for sovereign listening in the age of synthetic AI voices.',
    image: '/images/dog_listening_combined.png',
    imageAlt: 'Historical Nipper Gramophone and Painted Blue Roadside Dog Statue composite illustration',
    relatedTransmissions: [
      {
        id: 'node-harlem-505-w147-studio',
        eyebrow: 'Prime Node · Southern Counterweight (Coordinate 505)',
        title: '503–505 West 147th Street (Coordinate 505 // The Plateo Twin-Lot)',
        description:
          'Inside the 1901 invisible territory in Harlem where railroad bridge builder Levi Penfield Treadwell mapped the corridor at No. 503 and horologist Eugene L. Fitch invented the revolving brass Plato Clock under a submerged glass dome at No. 505.',
        path: '/passport/transmissions/node-harlem-505-w147-studio',
      },
      {
        id: 'venue-turbo-haus-montreal',
        eyebrow: 'Montréal Corridor Room',
        title: 'Turbo Haus & Bily Kun: Montreal Field Transmission',
        description:
          'Live stage entanglement along the Montréal corridor where the Laurentian Field Note arrives in the room.',
        path: '/passport/transmissions/venue-turbo-haus-montreal',
      },
    ],
    crewCritique: [
      {
        ...CREW.togee,
        line:
          'A sculpted dog painted Quaker-blue, drifted over with white summer clouds and peace signs along the New Jersey shoulder: in our network’s architecture, this roadside sentinel is the moving ear of the corridor—the northern counterweight to our fixed engine room at Coordinate 505 in Harlem. It stands right where the corporate voice ends and the community signal begins.',
      },
      {
        ...CREW.lex,
        line:
          'Every living transmission circuit needs two anchors: a clock to mark the moment, and an ear to witness the echo. Down inside Harlem’s 1901 invisible territory at 503–505 West 147th Street, Levi Penfield Treadwell drafted stone railroad bridges across space while Eugene L. Fitch dropped numbered brass plates of witnessed time. Out on the open corridor toward the Laurentian shield, this painted blue dog is the receiver catching the signal in the dark.',
      },
      {
        ...CREW.anansi,
        line:
          'In 1898—just three years before Fitch sat under gaslight in Harlem filing his Plato Clock patent—Francis Barraud painted Nipper staring into a phonograph horn for His Master’s Voice, stamped onto millions of shellac 78s from Camden to Montréal’s St-Henri Berliner and RCA Victor factory. Just as Fitch refused to turn his falling plates into ad boards and submerged his clockwork in a water-filled glass dome, the clouds painted over this roadside dog strip away the master’s phonograph and tune Nipper to the 1854 Frequency.',
      },
      {
        ...CREW.kaelen,
        line:
          'Listen to the two frequencies meeting across the corridor: the 60-cycle burner hum and Eighth Avenue A-train rumble beneath 147th Street where we press 300gsm field cards, answered by the warm 78 RPM shellac crackle dissolving into open highway wind toward the Tomifobia River. The Harlem Node builds the clock; the Laurentian Field Note listens to the dark between the ticks.',
      },
    ],
    closing:
      'From the 1901 invisible territory at Coordinate 505 in Harlem, past the Quaker-blue sentinel in New Jersey, to the old brick walls of St-Henri in Montréal: refuse the master’s horn, keep the water lens clear, and tune in to the dog still listening.',
    provenance: {
      sourceAuthority: 'sovereign_field_archive',
      eventDate: '2026-11-04',
      nodeCoordinate: 'New Jersey Roadside → Hudson & Laurentian Shield Corridor (Counterweight to 503–505 W 147th St, Harlem & Lenoir St, St-Henri, Montréal)',
      capturedBy: ['TogeeVision Roadside Field Unit'],
      sourceMaterials: [
        'Quaker-Blue Roadside Peace-Dog Optical & Acoustic Field Capture (New Jersey Corridor → Laurentian Borderlands)',
        'Francis Barraud 1898 Painting & His Master’s Voice / Emile Berliner Gramophone Archive (Camden, NJ & Lenoir Street, St-Henri, Montréal RCA Victor Museum)',
        'Counterweight Node Cross-Reference: US_PATENT_716209_1902 (Eugene L. Fitch Revolving Brass Plato Clock & Water-Filled Glass Dome at 505 W 147th St) & Levi Penfield Treadwell Rail Engineering at 503 W 147th St',
        'Prime Laurentian Field Note Master Audio EN (transmission_field-the-dog-still-listening.mp3, 00:04:41.08, 44.1kHz stereo, SHA-256: dada5496c76d3dfbd095b936445731a35156730f0ff0fa5065a03ef6ddc3c329)',
        'Prime Laurentian Field Note Master Audio FR-CA (transmission_field-the-dog-still-listening_fr-ca.mp3, 00:05:15.04, 44.1kHz stereo, SHA-256: 3074d5d94f8c33785cfb88358dde1773a30fb1e2fd22828572ecea5bf97015d4)',
      ],
      rightsStatus: 'sovereign_field_archive',
    },
    acousticTelemetry: {
      duration: '00:04:41.08',
      sampleRate: '44100 Hz',
      channels: 'stereo',
      meanVolumeDb: -14.0,
      peakVolumeDb: -1.0,
      chapters: [
        {
          chapter: 1,
          timecode: '00:00:00',
          title: 'The Quaker-Blue Sentinel & The Moving Corridor Ear',
          act: 'TogeeVision & 78 RPM Shellac Groove',
          energyLevelDb: -14.0,
        },
        {
          chapter: 2,
          timecode: '00:01:00',
          title: 'The Clock & The Ear (From Harlem’s 1901 Invisible Territory to the Laurentian Shield)',
          act: 'Lex Gupta, Fitch & Treadwell',
          energyLevelDb: -13.9,
        },
        {
          chapter: 3,
          timecode: '00:02:05',
          title: 'From Barraud’s 1898 Nipper & St-Henri’s Berliner Factory to Fitch’s Submerged Dome',
          act: 'Anansi Byte & The 1854 Puddle Filter',
          energyLevelDb: -13.7,
        },
        {
          chapter: 4,
          timecode: '00:03:29',
          title: 'Listening to the Dark Between the Ticks & 1854 Frequency Sign-Off',
          act: 'Kaelen Vance & TogeeVision',
          energyLevelDb: -14.0,
        },
      ],
    },
    visualAssets: [
      {
        id: 'photo-dog-still-listening-nipper-dialogue',
        type: 'image',
        src: '/images/dog_listening_combined.png',
        title: 'The Dog Still Listening // Roadside Nipper Dialogue (1898–2026)',
        caption:
          'Quaker-blue roadside dog painted with summer clouds and peace signs in New Jersey paired with Francis Barraud’s 1898 painting of Nipper and Montréal St-Henri’s Berliner / RCA Victor gramophone heritage.',
      },
    ],
    mediaArchaeology: {
      description:
        'Bridging the New Jersey roadside, the 1901 invisible territory at 503–505 West 147th Street in Harlem (where Levi Penfield Treadwell drafted stone railroad bridges and Eugene L. Fitch submerged his revolving brass Plato Clock inside a water-filled glass dome), and Montréal’s historic St-Henri Berliner / RCA Victor gramophone factory, this Prime Laurentian Field Note strips away the corporate "His Master’s Voice" myth and tunes the corridor to sovereign listening across the Laurentian shield and Tomifobia River.',
      highlight:
        'The Clock & The Ear: Prime Laurentian Field Note (Northern Anchor) answering Coordinate 505’s falling brass time-plates by listening to the dark between the ticks.',
    },
  },
};

const NODE_TRANSMISSION_CHRONICLES = {
  'node-harlem-505-w147-studio': {
    id: 'node-harlem-505-w147-studio',
    chronicleId: 'transmission_node-harlem-505-w147-studio',
    sourceType: 'node',
    pillar: 'node',
    isPrimeAnchor: true,
    primeBadge: 'Prime Node · Coordinate 505',
    counterweightFieldId: 'field-the-dog-still-listening',
    sourceId: 'harlem-505-w147-studio',
    city: 'nyc',
    title: '503–505 West 147th Street: Coordinate 505 // The Plateo Twin-Lot',
    eyebrow: 'Prime Node · Harlem Production & Archival Engine Room',
    subject: '503–505 West 147th Street (Coordinate 505 // Eugene L. Fitch & Levi P. Treadwell)',
    status: 'Sovereign Studio Node Active · Southern Anchor',
    transmission: 'The Plateo Mechanism // Coordinate 505',
    sourcePath: '/nodes/harlem-505-w147-studio',
    radarPath: '/passport/radar?city=nyc',
    image: '/images/transmissions/nyc/harlem-505-w147-plato-clock-coordinate-505.jpg',
    imageAlt:
      'Eugene L. Fitch 1902 glass-domed Plato Clock revolving brass flip-plate mechanism set to 5:05 beside Levi Penfield Treadwell railroad bridge blueprints and a 300gsm Coordinate 505 field plate at 503–505 West 147th Street',
    audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_node-harlem-505-w147-studio.mp3'),
    fallbackAudioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_node-harlem-505-w147-studio.mp3'),
    audioMetaPath: '/api/chronicle/audio-meta/transmission_node-harlem-505-w147-studio',
    soundCue:
      'Low 60-cycle basement burner hum and deep Eighth Avenue A-train bedrock rumble beneath 147th Street, punctuated by the crisp metallic flip-snap of Eugene L. Fitch’s 1902 revolving brass Plato Clock plates.',
    openingNarrative:
      'Prime Node transmission locked at Coordinate 505: 503–505 West 147th Street in Harlem. Ask the city when these twin five-story walk-ups were born and the official ledger says 1930—thirty years late—while preservation catalogs cross-wire the address with the library on 145th Street and twin facades down on 140th and 141st. That 1901 construction confusion carves out invisible territories right inside the Manhattan grid: a thirty-year blind spot where Eugene L. Fitch sat under gaslight at No. 505 inventing the revolving brass Plato Clock beside railroad bridge builder Levi Penfield Treadwell at No. 503.',
    about:
      'Standing on the ridge of Sugar Hill and Hamilton Heights at 503–505 West 147th Street, Coordinate 505 is the Southern Prime Node of the TogeeVision circuit. Operating inside the invisible territories created by the 1901 construction confusion, the studio unites two neighbors who shared a party wall at the turn of the century: railroad civil engineer Levi Penfield Treadwell at No. 503, who spent his life spanning rivers across space, and solitary horologist Eugene L. Fitch at No. 505, who replaced sweeping clock hands with falling brass plates of witnessed time.',
    workshopSignificance:
      'Because the 1901 construction confusion left a thirty-year gap in the municipal ledger and cross-wired the address with neighboring streets, 503–505 West 147th Street functions as a real-world invisible territory—a pocket inside the grid where a pirate station can breathe outside the algorithm’s reach. When merchants begged Fitch to sell the backs of his falling clock plates as miniature billboards, he refused and instead submerged the clockwork inside a water-filled double-walled glass dome—the physical ancestor of the 1854 Paris Protocol’s Puddle Filter.',
    carriers: {
      en: {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_node-harlem-505-w147-studio.mp3'),
        audioReady: true,
      },
      'fr-CA': {
        audioUrl: toGcsPublicUrl(GCS_PUBLIC_BUCKET, 'audio/transmission_node-harlem-505-w147-studio_fr-ca.mp3'),
        audioReady: true,
      },
    },
    translations: {
      'fr-CA': {
        primeBadge: 'Nœud principal · Coordonnée 505',
        title: '503–505 West 147th Street : Coordonnée 505 // Le lot jumeau Plateo',
        eyebrow: 'Nœud principal · Salle des machines et d’archives de Harlem',
        subject: '503–505 West 147th Street (Coordonnée 505 // Eugene L. Fitch et Levi P. Treadwell)',
        status: 'Nœud studio souverain actif · Ancre sud',
        transmission: 'Le mécanisme Plateo // Coordonnée 505',
        imageAlt:
          'Horloge Plato 1902 d’Eugene L. Fitch à lamelles de laiton rotatives réglée sur 5:05 à côté des plans de ponts ferroviaires de Levi Penfield Treadwell et d’une plaque de terrain Coordonnée 505 au 503–505 West 147th Street',
        soundCue:
          'Bourdonnement à 60 cycles du brûleur au sous-sol et grondement souterrain de la ligne A sous la 147e rue, ponctués par le claquement métallique net des plaques de laiton rotatives de la Plato Clock de 1902.',
        openingNarrative:
          'Transmission du nœud principal verrouillée à la Coordonnée 505 : 503–505 West 147th Street à Harlem. Demandez à la ville quand ces immeubles jumeaux sont nés et le grand livre officiel répond 1930 — avec trente ans de retard — tandis que les catalogues patrimoniaux confondent l’adresse avec la bibliothèque de la 145e rue et les façades jumelles des 140e et 141e rues. Cette confusion de construction de 1901 ouvre des territoires invisibles au cœur même de la grille de Manhattan : un angle mort de trente ans où Eugene L. Fitch inventait l’horloge Plato à lamelles de laiton au n° 505, juste à côté du bâtisseur de ponts ferroviaires Levi Penfield Treadwell au n° 503.',
        about:
          'Dressée sur la crête de Sugar Hill et Hamilton Heights au 503–505 West 147th Street, la Coordonnée 505 est le nœud principal sud du circuit TogeeVision. Opérant au cœur des territoires invisibles créés par la confusion de construction de 1901, le studio réunit deux voisins séparés par un simple mur mitoyen au tournant du siècle : l’ingénieur ferroviaire Levi Penfield Treadwell au n° 503, qui lançait des ponts de pierre au-dessus des rivières, et l’horloger solitaire Eugene L. Fitch au n° 505, qui a remplacé les aiguilles continues par des plaques de laiton tombantes.',
        workshopSignificance:
          'Parce que la confusion de construction de 1901 a laissé un écart de trente ans dans les registres et brouillé l’adresse avec les rues voisines, le 503–505 West 147th Street agit comme un véritable territoire invisible — une poche hors de portée des algorithmes. Lorsque les marchands ont voulu transformer le dos de ses plaques d’horloge en panneaux publicitaires, Fitch a refusé et a immergé son mécanisme dans un dôme de verre à double paroi rempli d’eau — l’ancêtre direct du Filtre de Flaque du Protocole de Paris de 1854.',
        summary:
          'La salle des machines et d’archives de TogeeVision à Harlem : un territoire invisible créé par la confusion de construction de 1901 (debout dès 1899–1901 mais daté de 1930 par la ville), où l’horloger Eugene L. Fitch a créé la première horloge mécanique à lamelles (« Plato Clock », 1902) au n° 505 à côté du bâtisseur de ponts ferroviaires Levi Penfield Treadwell au n° 503.',
        crewCritique: [
          {
            ...CREW.togee,
            line:
              'La ville prétend que cette coque de brique et de calcaire est née en 1930 et confond ses portes avec celles des 145e, 140e et 141e rues. Cet écart de trente ans ouvre un territoire invisible où une station pirate peut respirer et estampiller le temps témoigné.',
          },
          {
            ...CREW.lex,
            line:
              'En 1901, deux hommes vivaient ici de part et d’autre du mur mitoyen : au 503, l’ingénieur Levi Penfield Treadwell dessinait des ponts de pierre et des rails à travers l’espace ; au 505, l’horloger Eugene L. Fitch cherchait à rendre le temps visible sans aiguilles. Un bâtiment traçait le corridor, l’autre mesurait la seconde.',
          },
          {
            ...CREW.anansi,
            line:
              'Sous le bec de gaz à l’hiver 1901, Fitch a incliné son axe de laiton vers l’arrière pour que la gravité fasse tomber chaque plaque au moment précis. Il a refusé d’y vendre de la réclame et a plongé l’horloge dans un dôme de verre rempli d’eau — reliant ce territoire invisible de Harlem au chien bleu qui écoute au nord.',
          },
          {
            ...CREW.kaelen,
            line:
              'Posez la main sur la colonne montante en fonte : le bourdonnement à 60 cycles du brûleur au sous-sol s’accorde au grondement souterrain du métro A sous la 147e rue, pendant que nous pressons nos plaques de terrain 300 g/m².',
          },
        ],
        closing:
          'Coordonnée 505 verrouillée. Du territoire invisible de 1901 au 503–505 West 147th Street jusqu’au bouclier laurentien : nous construisons le corridor et nous estampillons le temps témoigné.',
        mediaArchaeology: {
          description:
            'Au cœur des territoires invisibles créés par la confusion de construction de 1901 (écart de trente ans entre 1901 et 1930 et adresses croisées avec les 145e, 140e et 141e rues), Eugene L. Fitch a conçu au 505 West 147th Street l’horloge à plaques de laiton rotatives (« Plato Clock », 1902) sous un dôme de verre rempli d’eau, à côté du bâtisseur de ponts ferroviaires Levi Penfield Treadwell au n° 503.',
          highlight:
            'Nœud principal (Ancre sud) : Territoires invisibles de 1901, l’horloger et le bâtisseur de ponts (Fitch et Treadwell), dôme d’eau anti-réclame et plaques souveraines 300 g/m².',
        },
        acousticTelemetry: {
          duration: '00:04:23.71',
          sampleRate: '44100 Hz',
          channels: 'stereo',
          meanVolumeDb: -14.0,
          peakVolumeDb: -1.0,
          chapters: [
            {
              chapter: 1,
              timecode: '00:00:00',
              title: 'L’écart de trente ans et les territoires invisibles de la confusion de 1901',
              act: 'TogeeVision et plancher souterrain 60Hz',
              energyLevelDb: -14.0,
            },
            {
              chapter: 2,
              timecode: '00:01:09',
              title: 'L’horloger et le bâtisseur de ponts (503 et 505 West 147th Street)',
              act: 'Lex Gupta et l’instrument à mur mitoyen',
              energyLevelDb: -13.9,
            },
            {
              chapter: 3,
              timecode: '00:02:08',
              title: 'La Plato Clock d’Eugene L. Fitch (1902) et le dôme de verre immergé',
              act: 'Anansi Byte et plaques de laiton rotatives',
              energyLevelDb: -13.7,
            },
            {
              chapter: 4,
              timecode: '00:03:22',
              title: 'Pulsation souterraine de la ligne A et plaques souveraines 300 g/m²',
              act: 'Kaelen Vance et TogeeVision',
              energyLevelDb: -14.0,
            },
          ],
        },
        visualAssets: [
          {
            id: 'photo-harlem-505-plato-clock-coordinate',
            type: 'image',
            src: '/images/transmissions/nyc/harlem-505-w147-plato-clock-coordinate-505.jpg',
            title: 'Le mécanisme Plateo // Coordonnée 505 (503–505 West 147th Street)',
            caption:
              'Assemblage à plaques de laiton rotatives de la Plato Clock 1902 d’Eugene L. Fitch réglé sur 5:05 aux côtés des plans de ponts ferroviaires de Levi Penfield Treadwell et d’une plaque de terrain 300 g/m² Coordonnée 505 au 503–505 West 147th Street.',
          },
        ],
      },
    },
    summary:
      'TogeeVision’s fixed infrastructural and archival engine room in Harlem: operating inside the invisible territories created by the 1901 construction confusion (standing by 1899–1901 yet logged thirty years late as 1930), where clockmaker Eugene L. Fitch created the world’s first mechanical digital flip-clock ("Plato Clock," 1902) at No. 505 right next door to railroad bridge builder Levi Penfield Treadwell at No. 503.',
    relatedTransmissions: [
      {
        id: 'field-the-dog-still-listening',
        eyebrow: 'Prime Laurentian Field Note · Northern Counterweight',
        title: 'The Dog Still Listening: Transmission Chronicle',
        description:
          'The moving ear of the corridor: a Quaker-blue roadside dog painted with clouds and peace signs answering Coordinate 505’s falling brass time-plates and reclaiming St-Henri’s Berliner / RCA Victor Nipper emblem for the 1854 Frequency.',
        path: '/passport/transmissions/field-the-dog-still-listening',
      },
      {
        id: 'venue-minute-mapp-montreal',
        eyebrow: 'Public Civic Node · Mile End, Montréal',
        title: 'MINUTE_MAPP 2026: CROSSROADS Projection Mapping',
        description:
          'Public urban canvas beneath the Van Horne Viaduct and Skatepark where 60-second architectural mappings illuminate the former Million Carpets facade.',
        path: '/passport/transmissions/venue-minute-mapp-montreal',
      },
      {
        id: 'venue-kismat-washington-heights',
        title: 'Kismat Washington Heights: Transmission Chronicle',
        path: '/passport/transmissions/venue-kismat-washington-heights',
      },
      {
        id: 'venue-film-forum-nyc',
        title: 'Film Forum & The A Train: Once Upon A Time in Harlem Field Transmission',
        path: '/passport/transmissions/venue-film-forum-nyc',
      },
    ],
    crewCritique: [
      {
        ...CREW.togee,
        line:
          'The city claims this brick and limestone hull was laid down in 1930 and cross-wires its doors with 145th, 140th, and 141st Streets. That thirty-year gap opens an invisible territory where a pirate station can breathe and stamp discrete plates of witnessed time.',
      },
      {
        ...CREW.lex,
        line:
          'In 1901, two neighbors lived on opposite sides of this party wall: at No. 503, civil engineer Levi Penfield Treadwell drafted stone bridges and rail lines across space; at No. 505, horologist Eugene L. Fitch obsessed over how to make time visible without clock hands. One side mapped the corridor; the other side measured the second.',
      },
      {
        ...CREW.anansi,
        line:
          'Under gaslight in the winter of 1901, Fitch tilted his brass spindle backward so gravity would flip each numbered plate clean when its moment arrived. When merchants begged to turn his falling plates into ad boards, he refused and submerged the clockwork inside a water-filled glass dome—linking Harlem’s invisible territory north to the painted blue dog listening in the dark.',
      },
      {
        ...CREW.kaelen,
        line:
          'Put your palm against the cast-iron riser: the 60-cycle hum of the basement burner locks into phase with the deep Eighth Avenue A-train rumble shaking the bedrock beneath 147th Street while we press 300gsm sovereign field plates.',
      },
    ],
    closing:
      'Coordinate 505 is locked. From the 1901 invisible territory at 503–505 West 147th Street to the Laurentian shield: we build the corridor, and we stamp discrete plates of witnessed time.',
    provenance: {
      sourceAuthority: 'sovereign_field_archive',
      eventDate: '2026-11-04',
      nodeCoordinate: '503–505 West 147th Street, Sugar Hill / Hamilton Heights, Harlem, NY 10031',
      capturedBy: ['TogeeVision Coordinate 505 Studio Unit'],
      sourceMaterials: [
        'US_PATENT_716209_1902 (Eugene L. Fitch, Time Indicator / Plato Clock, filed Jan 3, 1901; granted Dec 16, 1902 from 505 West 147th Street)',
        'NYC_DIR_1902_FITCH_505_W147 & TREADWELL_503_W147 (Trow’s General Directory of the Boroughs of Manhattan and Bronx, 1901–1903 — proving 1899–1901 occupancy vs. 1930 municipal ghost date)',
        'ARCHIVAL_DECOY_CONFLATION_145_140_141 (Preservation catalog cross-indexing of 503–505 W 147th St with 503–505 W 145th St library & 140th/141st St twin structures — creating the 1901 construction confusion & invisible territories)',
        'Coordinate 505 Archival Plate Capture (harlem-505-w147-plato-clock-coordinate-505.jpg, SHA-256: d55184ca4699edcb0dc3070c32a06bc4c426796709f8950526c8b76687efe003)',
        'Coordinate 505 Master Audio EN (transmission_node-harlem-505-w147-studio.mp3, 00:03:52.36, 44.1kHz stereo, SHA-256: 0bc0d9de0f10d077b2f305d707ef360b014525b370e73d23aacacb5dc67c9c0e)',
        'Coordinate 505 Master Audio FR-CA (transmission_node-harlem-505-w147-studio_fr-ca.mp3, 00:04:23.71, 44.1kHz stereo, SHA-256: cc26af22704ba1ab267a52bc19d8df59b21b7d802a32497c703eaef4fa2fdafe)',
      ],
      rightsStatus: 'sovereign_field_archive',
    },
    acousticTelemetry: {
      duration: '00:03:52.36',
      sampleRate: '44100 Hz',
      channels: 'stereo',
      meanVolumeDb: -14.0,
      peakVolumeDb: -1.0,
      chapters: [
        {
          chapter: 1,
          timecode: '00:00:00',
          title: 'The Thirty-Year Gap & Invisible Territories of the 1901 Construction Confusion',
          act: 'TogeeVision & 60Hz Subterranean Baseline',
          energyLevelDb: -14.0,
        },
        {
          chapter: 2,
          timecode: '00:01:01',
          title: 'The Clockmaker & The Bridge Builder (503 & 505 West 147th Street)',
          act: 'Lex Gupta & The Twin-Wall Instrument',
          energyLevelDb: -13.9,
        },
        {
          chapter: 3,
          timecode: '00:01:55',
          title: 'Eugene L. Fitch’s 1902 Plato Clock & The Submerged Glass Dome',
          act: 'Anansi Byte & Revolving Brass Flip-Plates',
          energyLevelDb: -13.7,
        },
        {
          chapter: 4,
          timecode: '00:02:59',
          title: 'Subterranean A-Train Pulse & 300gsm Sovereign Field Plates',
          act: 'Kaelen Vance & TogeeVision',
          energyLevelDb: -14.0,
        },
      ],
    },
    visualAssets: [
      {
        id: 'photo-harlem-505-plato-clock-coordinate',
        type: 'image',
        src: '/images/transmissions/nyc/harlem-505-w147-plato-clock-coordinate-505.jpg',
        title: 'The Plateo Mechanism // Coordinate 505 (503–505 West 147th Street)',
        caption:
          'Eugene L. Fitch’s 1902 glass-domed Plato Clock revolving brass flip-plate assembly set to 5:05 beside Levi Penfield Treadwell’s railroad bridge blueprints and a 300gsm Coordinate 505 field plate at 503–505 West 147th Street.',
      },
    ],
    mediaArchaeology: {
      description:
        'Operating inside the invisible territories created by the 1901 construction confusion—where official ledgers log 1930 (thirty years late) and preservation catalogs cross-index 503–505 West 147th Street with 145th, 140th, and 141st Street decoys—horologist Eugene L. Fitch invented the revolving brass flip-plate Plato Clock (1902) and submerged the clockwork inside a water-filled double-walled glass dome, while civil engineer Levi Penfield Treadwell mapped stone railroad bridges next door at No. 503.',
      highlight:
        'Prime Node (Southern Anchor): Invisible territories of the 1901 construction confusion, uniting Fitch’s 1902 falling brass time-plates and Treadwell’s heavy corridor rails as the infrastructural counterweight to The Dog Still Listening.',
    },
  },
  'venue-minute-mapp-montreal': MINUTE_MAPP_MONTREAL_TRANSMISSION,
};

const NODE_TRANSMISSION_ALIASES = {
  'harlem-505-w147-studio': NODE_TRANSMISSION_CHRONICLES['node-harlem-505-w147-studio'],
  'venue-harlem-505-w147-studio': NODE_TRANSMISSION_CHRONICLES['node-harlem-505-w147-studio'],
  'coordinate-505': NODE_TRANSMISSION_CHRONICLES['node-harlem-505-w147-studio'],
  '505-west-147th': NODE_TRANSMISSION_CHRONICLES['node-harlem-505-w147-studio'],
  '503-505-west-147th': NODE_TRANSMISSION_CHRONICLES['node-harlem-505-w147-studio'],
  'plato-clock': NODE_TRANSMISSION_CHRONICLES['node-harlem-505-w147-studio'],
  'node-minute-mapp-montreal': MINUTE_MAPP_MONTREAL_TRANSMISSION,
  'node-minute-mapp': MINUTE_MAPP_MONTREAL_TRANSMISSION,
};

export const getArtistTransmissionChronicleId = (profileId) => `artist-${profileId}`;

export const getVenueTransmissionChronicleId = (venueId) => `venue-${venueId}`;

export const getNodeTransmissionChronicleId = (nodeId) => `node-${nodeId}`;

export const listTransmissionChronicles = (langCode = 'en') => {
  const rawList = [
    ...Object.values(FIELD_TRANSMISSION_CHRONICLES),
    ...Object.values(NODE_TRANSMISSION_CHRONICLES),
    ...Object.values(VENUE_TRANSMISSION_CHRONICLES),
    ...Object.values(ARTIST_PROFILES).map(buildArtistTransmissionChronicle).filter(Boolean),
  ];

  const seenIds = new Set();
  const baseList = [];
  for (const item of rawList) {
    const key = item.id || item.chronicleId;
    if (key && !seenIds.has(key)) {
      seenIds.add(key);
      baseList.push(item);
    }
  }

  if (!isFrenchLanguage(langCode)) return baseList;

  return baseList.map((record) => {
    const localized = record.translations?.['fr-CA'];
    const carrierAudio = record.carriers?.['fr-CA']?.audioUrl;
    const hasQcAudio = isQuebecoisAudioAvailable(record);
    const qcAudioUrl = hasQcAudio ? (carrierAudio || record.audioUrl) : null;
    if (localized || carrierAudio) {
      return {
        ...record,
        ...(localized || {}),
        language: 'fr-CA',
        audioUrl: qcAudioUrl,
        fallbackAudioUrl: qcAudioUrl,
      };
    }
    return {
      ...record,
      language: 'fr-CA',
      audioUrl: qcAudioUrl,
      fallbackAudioUrl: qcAudioUrl,
    };
  });
};

export const getTransmissionChronicle = (chronicleId, langCode = 'en') => {
  if (!chronicleId) return null;
  const rawId = String(chronicleId).trim();
  const strippedId = rawId.replace(/^transmission_/, '');

  let record = null;
  if (VENUE_TRANSMISSION_CHRONICLES[rawId]) {
    record = VENUE_TRANSMISSION_CHRONICLES[rawId];
  } else if (VENUE_TRANSMISSION_CHRONICLES[strippedId]) {
    record = VENUE_TRANSMISSION_CHRONICLES[strippedId];
  } else if (VENUE_TRANSMISSION_ALIASES[rawId]) {
    record = VENUE_TRANSMISSION_ALIASES[rawId];
  } else if (VENUE_TRANSMISSION_ALIASES[strippedId]) {
    record = VENUE_TRANSMISSION_ALIASES[strippedId];
  } else if (NODE_TRANSMISSION_CHRONICLES[rawId]) {
    record = NODE_TRANSMISSION_CHRONICLES[rawId];
  } else if (NODE_TRANSMISSION_CHRONICLES[strippedId]) {
    record = NODE_TRANSMISSION_CHRONICLES[strippedId];
  } else if (NODE_TRANSMISSION_ALIASES[rawId]) {
    record = NODE_TRANSMISSION_ALIASES[rawId];
  } else if (NODE_TRANSMISSION_ALIASES[strippedId]) {
    record = NODE_TRANSMISSION_ALIASES[strippedId];
  } else if (FIELD_TRANSMISSION_CHRONICLES[rawId]) {
    record = FIELD_TRANSMISSION_CHRONICLES[rawId];
  } else if (FIELD_TRANSMISSION_CHRONICLES[strippedId]) {
    record = FIELD_TRANSMISSION_CHRONICLES[strippedId];
  } else if (FIELD_TRANSMISSION_CHRONICLES[`transmission_${strippedId}`]) {
    record = FIELD_TRANSMISSION_CHRONICLES[`transmission_${strippedId}`];
  } else {
    const artistMatch = strippedId.match(/^artist-(.+)$/);
    const honeychildAlias = /^(?:pianos-honeychild-coleman|honeychild-coleman-pianos-nyc|honeychilds-dem|honeychild-dem|the-salt-pianos-nyc)$/i.test(strippedId);
    if (honeychildAlias) {
      record = buildArtistTransmissionChronicle(ARTIST_PROFILES['honeychild-coleman']);
    } else if (artistMatch) {
      record = buildArtistTransmissionChronicle(ARTIST_PROFILES[artistMatch[1]]);
    } else if (ARTIST_PROFILES[strippedId]) {
      record = buildArtistTransmissionChronicle(ARTIST_PROFILES[strippedId]);
    } else {
      record = listTransmissionChronicles('en').find(
        (transmission) => transmission.chronicleId === rawId || transmission.id === rawId || transmission.id === strippedId || transmission.chronicleId === `transmission_${strippedId}`
      ) || null;
    }
  }

  if (!record) return null;

  if (isFrenchLanguage(langCode)) {
    const localized = record.translations?.['fr-CA'];
    const carrierAudio = record.carriers?.['fr-CA']?.audioUrl;
    const hasQcAudio = isQuebecoisAudioAvailable(record);
    const qcAudioUrl = hasQcAudio ? (carrierAudio || record.audioUrl) : null;
    if (localized || carrierAudio) {
      return {
        ...record,
        ...(localized || {}),
        language: 'fr-CA',
        audioUrl: qcAudioUrl,
        fallbackAudioUrl: qcAudioUrl,
      };
    }
    return {
      ...record,
      language: 'fr-CA',
      audioUrl: qcAudioUrl,
      fallbackAudioUrl: qcAudioUrl,
    };
  }

  return {
    ...record,
    language: 'en',
  };
};

export const formatTransmissionTitle = (rawTitle, canonicalSource = '') => {
  const str = String(rawTitle || canonicalSource || '').trim();
  if (!str) return 'Field Signal';

  // Specific canonical venue/artist/node checks
  if (/503[-–—_\s]*505|505[-_\s]*w(?:est)?[-_\s]*147|coordinate[-_\s]*505|plato[-_\s]*clock|plateo/i.test(str)) return "503–505 W 147th St (Coordinate 505)";
  if (/dog[-_\s]*still[-_\s]*listening|chien[-_\s]*qui[-_\s]*[eé]coute/i.test(str)) return "The Dog Still Listening (Laurentian)";
  if (/escogriffe|lesco/i.test(str)) return "Bar L'Escogriffe";
  if (/turbo[-_\s]*haus/i.test(str)) return "Turbo Haus (Montréal)";
  if (/minute[-_\s]*mapp|mapp[-_\s]*(?:mtl|festival)|million[-_\s]*carpets/i.test(str)) return "MINUTE_MAPP (Montréal)";
  if (/parcel[-_\s]*5|hallock/i.test(str)) return "Hallock Lounge (Rochester)";
  if (/victoria/i.test(str)) return "Victoria (NYC)";
  if (/pianos|dream[-_\s]*pop|the[-_\s]*veldt/i.test(str)) return "Pianos (NYC)";
  if (/nomaa|dear[-_\s]*earth|paula[-_\s]*maria[-_\s]*persiani|aqui[-_\s]*me[-_\s]*quedo/i.test(str)) return "NoMAA Gallery (Uptown NYC)";
  if (/film[-_\s]*forum|once[-_\s]*upon[-_\s]*a[-_\s]*time[-_\s]*in[-_\s]*harlem/i.test(str)) return "Film Forum (NYC)";
  if (/kismat/i.test(str)) return "Kismat (Washington Heights)";
  if (/rastafari/i.test(str)) return "House of Rastafari";
  if (/steelstacks/i.test(str)) return "SteelStacks";
  if (/the[-_\s]*nick/i.test(str)) return "The Nick (Birmingham)";
  if (/dial[-_\s]*back/i.test(str)) return "Dial Back Sound";
  if (/azariah/i.test(str)) return "Azariah (Rochester)";
  if (/monk[-_\s]*(?:americana|washington)/i.test(str)) return "Monk Americana (NYC)";
  if (/anansi[-_\s]*byte/i.test(str)) return "Anansi Byte (NYC)";
  if (/honeychild[-_\s]*coleman/i.test(str)) return "Honeychild Coleman (NYC)";

  // Strip generic / trailing field transmission suffixes
  let cleaned = str
    .replace(/[:–—\-]\s*(?:Montreal\s+|NYC\s+|Rochester\s+)?(?:Field\s+)?(?:Transmission|Chronique)(?:\s+Chronicle)?.*$/i, '')
    .replace(/[:–—\-]\s*Transmission\s+de\s+terrain.*$/i, '')
    .replace(/\s+(?:Field\s+)?(?:Transmission|Chronique)$/i, '')
    .trim();

  // If title was a slug like "venue-lescogriffe-montreal" or "artist-azariah"
  cleaned = cleaned.replace(/^(?:venue|artist|field|node)[-_]/i, '').replace(/[_-]/g, ' ');
  return cleaned || 'Field Signal';
};
